import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers } from '../../../store/slices/userSlice';
import CompactRichEditor, { setQuillHtml, toQuillCompatibleHtml } from '../../../components/common/CompactRichEditor';
import PresignedImage from '../../../components/common/PresignedImage';
import FileTypeIcon from '../../../components/common/FileTypeIcon';
import { API_URL } from '../../../config/api';
import { buildLegacyFileUrl, extractInternalFileKey } from '../../../services/fileUrlService';
import { uploadFilesWithProgress, inferAttachmentFileType } from '../../../utils/uploadFilesWithProgress';
import {
  extractUrlFromText,
  formatFileSize,
  isDisplayableImage,
  isLinkAttachment,
} from '../../../utils/fileUtils';
import {
  createNote,
  deleteNote,
  fetchCredentialBucket,
  fetchCredentialPinStatus,
  fetchLinkPreview,
  fetchMyNotes,
  fetchNote,
  rewriteNoteWithAi,
  saveCredentialBucket,
  setCredentialPin,
  updateNote,
  verifyCredentialPin,
  appendNeedToCallItem,
} from '../services/noteService';
import { notifyNeedToCallChanged } from '../../../utils/suggestedReplies';
import { matchPersonBySpokenName } from '../../../utils/voiceTaskCommands';
import { extractBuyingItemsFromNoteHtml, extractNeedToCallNamesFromNoteHtml, extractTaskItemsFromNoteHtml, personLabel } from '../../../utils/noteActionItems';
import CallReminderPeopleModal from './CallReminderPeopleModal';
import ShareNoteModal from './ShareNoteModal';
import { copyTextToClipboard } from '../../../utils/copyText';
import NoteAttachmentViewer from './NoteAttachmentViewer';
import NeedToCallList from './NeedToCallList';
import TaskCreationDialog from '../../../components/tasks/TaskCreationDialog';
import ConfirmModal from '../../../components/common/ConfirmModal';
import { isAudioCaptionNoteTitle } from '../../../utils/audioCaptionNote';

const AUTOSAVE_MS = 1200;

const newSensitiveId = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const emptySensitiveEntry = () => ({
  id: newSensitiveId('entry'),
  key: '',
  value: '',
  valueVisible: false,
});

const normalizeSensitiveAttachments = (raw) =>
  (Array.isArray(raw) ? raw : [])
    .filter((item) => item && (item.fileUrl || item.fileName))
    .map((item) => ({
      id: item.id || newSensitiveId('att'),
      fileName: String(item.fileName || 'file').trim() || 'file',
      fileUrl: item.fileUrl || item.url || item.fileKey || '',
      fileType: item.fileType || 'FILE',
      fileSize: item.fileSize || 0,
    }));

const normalizeSensitiveBlocks = (blocks) =>
  (Array.isArray(blocks) ? blocks : [])
    .map((block) => {
      const title = String(block?.title || block?.name || '').trim();
      let entries = Array.isArray(block?.entries) ? block.entries : [];
      if (entries.length === 0 && (block?.value || block?.name)) {
        entries = [{
          id: block.id || newSensitiveId('entry'),
          key: String(block.name || 'Secret').trim(),
          value: block.value || '',
        }];
      }
      return {
        id: block?.id || newSensitiveId('secret'),
        title,
        askPinOnCopy: Boolean(block?.askPinOnCopy),
        entries: entries
          .filter((entry) => entry && String(entry.key || '').trim())
          .map((entry) => ({
            id: entry.id || newSensitiveId('entry'),
            key: String(entry.key || '').trim(),
            value: entry.value || '',
            valueVisible: Boolean(entry.valueVisible),
          })),
        attachments: normalizeSensitiveAttachments(block?.attachments),
      };
    })
    .filter((block) => block.title);

const redactNoteForList = (note) => ({
  ...note,
  sensitiveBlocks: normalizeSensitiveBlocks(note?.sensitiveBlocks).map((block) => ({
    id: block.id,
    title: block.title,
    askPinOnCopy: Boolean(block.askPinOnCopy),
    entries: (block.entries || []).map((entry) => ({
      id: entry.id,
      key: entry.key,
      valueVisible: Boolean(entry.valueVisible),
    })),
    attachments: (block.attachments || []).map((item) => ({
      id: item.id,
      fileName: item.fileName,
      fileType: item.fileType,
      fileSize: item.fileSize,
    })),
  })),
});

const stableJson = (value) => JSON.stringify(value ?? null);

const snapshotNoteFields = (note) => ({
  id: note?.id || null,
  updatedAt: note?.updatedAt || null,
  title: note?.title || '',
  contentHtml: note?.contentHtml || '',
  originalContentHtml: note?.originalContentHtml || '',
  attachments: Array.isArray(note?.attachments) ? note.attachments : [],
  tags: normalizeNoteTags(note?.tags),
  sensitiveBlocks: normalizeSensitiveBlocks(note?.sensitiveBlocks),
});

const sameNoteText = (a, b) => String(a || '') === String(b || '');

const sameNoteJson = (a, b) => stableJson(a) === stableJson(b);

const liveFieldsChanged = (a, b) => {
  if (!a || !b) return true;
  return (
    !sameNoteText(a.title, b.title)
    || !sameNoteText(a.contentHtml, b.contentHtml)
    || !sameNoteJson(a.attachments, b.attachments)
    || !sameNoteJson(a.tags, b.tags)
    || !sameNoteJson(a.sensitiveBlocks, b.sensitiveBlocks)
  );
};

const escapeNoteHtml = (value) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const personDisplayName = (user) => {
  if (!user) return '';
  return [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.username || '';
};

const blockKey = (html) =>
  String(html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const isEmptyHtmlBlock = (html) => !blockKey(html);

const htmlToBlocks = (html) => {
  const source = String(html || '').trim();
  if (!source) return [];
  if (typeof document === 'undefined') return [source];
  const root = document.createElement('div');
  root.innerHTML = source;
  return [...root.childNodes]
    .map((node) => {
      if (node.nodeType === 3) {
        const text = String(node.textContent || '').trim();
        return text ? `<p>${escapeNoteHtml(text)}</p>` : '';
      }
      if (node.nodeType === 1 && node.tagName && node.tagName.toLowerCase() !== 'br') {
        return node.outerHTML;
      }
      return '';
    })
    .filter((block) => block && !isEmptyHtmlBlock(block));
};

const attributeRemoteBlock = (html, name) => {
  const label = escapeNoteHtml(String(name || 'Someone').trim() || 'Someone');
  const chip = `<strong style="color:#0369a1">${label}</strong> `;
  if (typeof document === 'undefined') {
    return `<p>${chip}${html}</p>`;
  }
  const root = document.createElement('div');
  root.innerHTML = html;
  const el = root.firstElementChild;
  if (el) {
    el.innerHTML = `${chip}${el.innerHTML}`;
    return root.innerHTML;
  }
  return `<p>${chip}${html}</p>`;
};

const mergeLiveHtml = (baseHtml, localHtml, remoteHtml, otherName) => {
  if (sameNoteText(localHtml, remoteHtml)) return localHtml || remoteHtml || '';
  if (sameNoteText(localHtml, baseHtml)) return remoteHtml || localHtml || '';
  if (sameNoteText(remoteHtml, baseHtml)) return localHtml || remoteHtml || '';

  const localBlocks = htmlToBlocks(localHtml);
  const remoteBlocks = htmlToBlocks(remoteHtml);
  if (!localBlocks.length) return remoteHtml || '';
  if (!remoteBlocks.length) return localHtml || '';

  const lKeys = localBlocks.map(blockKey);
  const rKeys = remoteBlocks.map(blockKey);
  const n = localBlocks.length;
  const m = remoteBlocks.length;
  const dp = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      dp[i][j] = lKeys[i] === rKeys[j]
        ? dp[i + 1][j + 1] + 1
        : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const merged = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (lKeys[i] === rKeys[j]) {
      merged.push(localBlocks[i]);
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      merged.push(localBlocks[i]);
      i += 1;
    } else {
      merged.push(attributeRemoteBlock(remoteBlocks[j], otherName));
      j += 1;
    }
  }
  while (i < n) {
    merged.push(localBlocks[i]);
    i += 1;
  }
  while (j < m) {
    merged.push(attributeRemoteBlock(remoteBlocks[j], otherName));
    j += 1;
  }
  return merged.join('') || localHtml || remoteHtml || '';
};

const unionNoteItems = (localItems, remoteItems, keyFn) => {
  const map = new Map();
  [...(Array.isArray(localItems) ? localItems : []), ...(Array.isArray(remoteItems) ? remoteItems : [])]
    .forEach((item) => {
      const key = keyFn(item);
      if (key && !map.has(key)) map.set(key, item);
    });
  return [...map.values()];
};

const parseSensitiveSeed = (raw) => {
  const lines = String(raw || '')
    .replace(/\u00a0/g, ' ')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) {
    return { title: '', entries: [emptySensitiveEntry()] };
  }
  const title = lines[0];
  const valueLines = lines.slice(1);
  if (!valueLines.length) {
    return { title, entries: [emptySensitiveEntry()] };
  }
  return {
    title,
    entries: valueLines.map((line) => {
      const dash = line.match(/^(.+?)\s+-\s+(.+)$/);
      const colon = !dash && line.match(/^(.+?)\s*:\s+(.+)$/);
      const eq = !dash && !colon && line.match(/^([^=]+)=(.+)$/);
      const pair = dash || colon || eq;
      return {
        id: newSensitiveId('entry'),
        key: pair ? pair[1].trim() : '',
        value: pair ? pair[2].trim() : line,
      };
    }),
  };
};

const EyeIcon = ({ off }) => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    {off ? (
      <>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.6 10.6A2 2 0 0012 14a2 2 0 001.4-.6M9.9 5.1A9.8 9.8 0 0112 5c5 0 9.3 3.1 11 7.5a11.6 11.6 0 01-4.2 5.1M6.1 6.1A11.5 11.5 0 001 12.5C2.7 16.9 7 20 12 20c1.4 0 2.7-.2 3.9-.7" />
      </>
    ) : (
      <>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
        <circle cx="12" cy="12" r="3" strokeWidth={2} />
      </>
    )}
  </svg>
);

const SensitiveField = ({
  value,
  onChange,
  placeholder,
  autoFocus,
  className = '',
  visible = false,
  onToggleVisible,
}) => (
  <div className={`relative min-w-0 ${className}`}>
    <input
      type={visible ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoComplete="off"
      autoFocus={autoFocus}
      className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pl-3 pr-8 py-2 text-sm text-gray-900 dark:text-white"
    />
    <button
      type="button"
      onClick={onToggleVisible}
      className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
      aria-label={visible ? 'Hide value' : 'Show value'}
    >
      <EyeIcon off={visible} />
    </button>
  </div>
);

const SensitiveBlockModal = ({
  open,
  seedText,
  initialBlock,
  pinEnabled,
  onClose,
  onSave,
}) => {
  const [title, setTitle] = useState('');
  const [entries, setEntries] = useState([emptySensitiveEntry()]);
  const [askPinOnCopy, setAskPinOnCopy] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const photoInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const isEdit = Boolean(initialBlock?.id);

  useEffect(() => {
    if (!open) return;
    setAskPinOnCopy(Boolean(initialBlock?.askPinOnCopy));
    setUploading(false);
    if (initialBlock) {
      setTitle(String(initialBlock.title || '').trim());
      const nextEntries = (Array.isArray(initialBlock.entries) ? initialBlock.entries : [])
        .map((entry) => ({
          id: entry.id || newSensitiveId('entry'),
          key: entry.key || '',
          value: entry.value || '',
          valueVisible: Boolean(entry.valueVisible),
        }));
      setEntries(nextEntries.length ? nextEntries : [emptySensitiveEntry()]);
      setAttachments(normalizeSensitiveAttachments(initialBlock.attachments));
      return;
    }
    const parsed = parseSensitiveSeed(seedText);
    setTitle(parsed.title);
    setEntries(parsed.entries.length ? parsed.entries : [emptySensitiveEntry()]);
    setAttachments([]);
  }, [open, seedText, initialBlock]);

  const uploadCredentialFiles = async (fileList) => {
    const list = Array.from(fileList || []).filter(Boolean);
    if (!list.length) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const results = await uploadFilesWithProgress({
        apiUrl: API_URL,
        token,
        files: list,
      });
      const uploaded = results.map((item, index) => {
        const data = item?.data || {};
        const file = list[index];
        return {
          id: data.id || newSensitiveId('att'),
          fileName: data.fileName || file?.name || 'file',
          fileUrl: data.fileUrl || data.fileKey || data.url || '',
          fileType: data.fileType || inferAttachmentFileType({ fileName: file?.name }),
          fileSize: data.fileSize || file?.size || 0,
        };
      }).filter((item) => item.fileUrl);
      if (uploaded.length) {
        setAttachments((prev) => [...prev, ...uploaded]);
      }
    } catch (err) {
      window.alert(err?.response?.data?.error || err?.message || 'Failed to attach file');
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;

  const completeEntries = entries
    .map((entry, index) => ({
      id: entry.id,
      key: entry.key.trim() || `item ${index + 1}`,
      value: entry.value.trim(),
      valueVisible: Boolean(entry.valueVisible),
    }))
    .filter((entry) => {
      if (!entry.key) return false;
      if (isEdit) return true;
      return Boolean(entry.value);
    });
  const canSave = title.trim() && (
    attachments.length > 0
    || (isEdit
      ? completeEntries.length > 0
      : completeEntries.some((entry) => entry.value))
  );

  const updateEntry = (entryId, patch) => {
    setEntries((prev) => prev.map((entry) => (
      entry.id === entryId ? { ...entry, ...patch } : entry
    )));
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label="Add credentials"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">
          {isEdit ? 'Update credentials' : 'Add credentials'}
        </h2>
        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
          {isEdit
            ? 'Change the title, names, or values. Leave a value blank to keep the current secret.'
            : 'Add a title, then name and value on each row. Use this for passwords and logins.'}
        </p>
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="title"
          className="mt-4 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
          autoFocus
        />
        <div className="mt-3 space-y-2">
          {entries.map((entry) => (
            <div key={entry.id} className="flex items-center gap-2">
              <input
                value={entry.key}
                onChange={(event) => updateEntry(entry.id, { key: event.target.value })}
                placeholder="name"
                autoComplete="off"
                className="w-[38%] flex-shrink-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
              />
              <SensitiveField
                className="flex-1"
                value={entry.value}
                visible={Boolean(entry.valueVisible)}
                onToggleVisible={() => updateEntry(entry.id, { valueVisible: !entry.valueVisible })}
                onChange={(event) => updateEntry(entry.id, { value: event.target.value })}
                placeholder={isEdit ? 'value (blank = keep)' : 'value'}
              />
              {entries.length > 1 ? (
                <button
                  type="button"
                  onClick={() => setEntries((prev) => prev.filter((item) => item.id !== entry.id))}
                  className="flex-shrink-0 text-gray-400 hover:text-rose-500 px-1 text-lg leading-none"
                  aria-label="Remove row"
                >
                  ×
                </button>
              ) : (
                <span className="w-5 flex-shrink-0" />
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => setEntries((prev) => [...prev, emptySensitiveEntry()])}
            className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline"
          >
            + add
          </button>
        </div>
        <div className="mt-4">
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(event) => {
              uploadCredentialFiles(event.target.files);
              event.target.value = '';
            }}
          />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(event) => {
              uploadCredentialFiles(event.target.files);
              event.target.value = '';
            }}
          />
          <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-200">Files</p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              disabled={uploading}
              onClick={() => photoInputRef.current?.click()}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
            >
              Photos
            </button>
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
            >
              Files
            </button>
            {uploading ? (
              <span className="self-center text-[11px] text-gray-500">Uploading…</span>
            ) : null}
          </div>
          {attachments.length ? (
            <ul className="mt-2 space-y-1">
              {attachments.map((item) => (
                <li key={item.id || item.fileUrl} className="flex items-center gap-2 text-[12px] text-gray-700 dark:text-gray-200">
                  <FileTypeIcon fileName={item.fileName} fileType={item.fileType} size="sm" />
                  <span className="min-w-0 flex-1 truncate">{item.fileName}</span>
                  <button
                    type="button"
                    onClick={() => setAttachments((prev) => prev.filter((file) => file.id !== item.id))}
                    className="text-gray-400 hover:text-rose-500 px-1"
                    aria-label={`Remove ${item.fileName}`}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
              Optional. Attach a screenshot, ID, or document.
            </p>
          )}
        </div>
        {pinEnabled ? (
          <label className="mt-4 flex items-start gap-2 text-[12px] text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              checked={askPinOnCopy}
              onChange={(event) => setAskPinOnCopy(event.target.checked)}
              className="mt-0.5 rounded border-gray-300 dark:border-gray-600"
            />
            <span>Ask PIN during copy. Opening attached files always asks for your PIN.</span>
          </label>
        ) : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSave}
            onClick={() => {
              if (!canSave) return;
              onSave({
                id: initialBlock?.id,
                title: title.trim(),
                entries: completeEntries,
                askPinOnCopy: Boolean(pinEnabled && askPinOnCopy),
                attachments,
              });
              onClose();
            }}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary-600 text-white disabled:opacity-50"
          >
            {isEdit ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CredentialRevealModal = ({ open, title, secret, onClose, onCopied }) => {
  const [mode, setMode] = useState('choose');

  useEffect(() => {
    if (!open) return;
    setMode('choose');
  }, [open, secret]);

  if (!open) return null;

  const label = String(title || 'Secret').trim() || 'Secret';

  const copySecret = async () => {
    try {
      await copyTextToClipboard(secret);
      onCopied?.();
      onClose();
    } catch {
      window.alert('Could not copy to clipboard');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[96] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label={mode === 'view' ? label : 'View or copy'}
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">{label}</h2>
        {mode === 'view' ? (
          <>
            <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800 px-3 py-3">
              <p className="whitespace-pre-wrap break-all font-mono text-sm text-gray-900 dark:text-gray-100">
                {secret}
              </p>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
              >
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              View it here, or copy it to the clipboard.
            </p>
            <div className="mt-5 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode('view')}
                className="px-3 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white"
              >
                View
              </button>
              <button
                type="button"
                onClick={copySecret}
                className="px-3 py-2 text-sm font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100"
              >
                Copy
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const pinDigits = (value) => String(value || '').replace(/\D/g, '').slice(0, 8);

const CredentialPinModal = ({ open, mode, onClose, onSuccess }) => {
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [currentPin, setCurrentPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [pinError, setPinError] = useState('');

  useEffect(() => {
    if (!open) return;
    setPin('');
    setConfirmPin('');
    setCurrentPin('');
    setBusy(false);
    setPinError('');
  }, [open, mode]);

  if (!open) return null;

  const isVerify = mode === 'verify';
  const isChange = mode === 'change';
  const pinOk = pin.length >= 4 && pin.length <= 8;
  const canSubmit = isVerify
    ? pinOk
    : pinOk && pin === confirmPin && (!isChange || currentPin.length >= 4);

  const title = isVerify
    ? 'Enter PIN'
    : isChange
      ? 'Change PIN'
      : 'Set a PIN';
  const description = isVerify
    ? 'Enter your PIN to continue.'
    : 'Make your credentials more secure and save from mistake deletion. Use 4 to 8 digits.';

  const submit = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    setPinError('');
    try {
      if (isVerify) {
        await verifyCredentialPin(pin);
      } else {
        await setCredentialPin({
          pin,
          currentPin: isChange ? currentPin : undefined,
        });
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setPinError(err?.response?.data?.error || err?.message || 'PIN failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h2>
        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">{description}</p>
        {isChange ? (
          <input
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={currentPin}
            onChange={(event) => setCurrentPin(pinDigits(event.target.value))}
            placeholder="current PIN"
            className="mt-4 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm tracking-[0.3em] text-gray-900 dark:text-white"
            autoFocus
          />
        ) : null}
        <input
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={pin}
          onChange={(event) => setPin(pinDigits(event.target.value))}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit();
          }}
          placeholder={isVerify ? 'PIN' : isChange ? 'new PIN' : 'PIN'}
          className={`${isChange ? 'mt-2' : 'mt-4'} w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm tracking-[0.3em] text-gray-900 dark:text-white`}
          autoFocus={!isChange}
        />
        {!isVerify ? (
          <input
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={confirmPin}
            onChange={(event) => setConfirmPin(pinDigits(event.target.value))}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit();
            }}
            placeholder="confirm PIN"
            className="mt-2 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm tracking-[0.3em] text-gray-900 dark:text-white"
          />
        ) : null}
        {pinError ? (
          <p className="mt-2 text-[11px] text-rose-600 dark:text-rose-400">{pinError}</p>
        ) : null}
        {!isVerify && pin && confirmPin && pin !== confirmPin ? (
          <p className="mt-2 text-[11px] text-rose-600 dark:text-rose-400">PINs do not match</p>
        ) : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit || busy}
            onClick={submit}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary-600 text-white disabled:opacity-50"
          >
            {busy ? 'Please wait' : isVerify ? 'Confirm' : isChange ? 'Update PIN' : 'Set PIN'}
          </button>
        </div>
      </div>
    </div>
  );
};

const resolveNoteLinkUrl = (raw) => {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return '';
  return extractUrlFromText(trimmed)
    || (/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
};

const urlFromClipboardText = (raw) => {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return '';
  const extracted = extractUrlFromText(trimmed);
  if (extracted) return extracted;
  const first = trimmed.split(/\s+/)[0];
  if (/^https?:\/\/[^\s]+/i.test(first)) return first;
  if (/^www\.[^\s]+\.[a-z]{2,}/i.test(first)) return `https://${first}`;
  return '';
};

const hostnameFromUrl = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url.replace(/^https?:\/\//i, '');
  }
};

const EditPencilIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

const AddLinkModal = ({
  open,
  seedUrl,
  onClose,
  onSave,
}) => {
  const [urlInput, setUrlInput] = useState('');
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const nameTouchedRef = useRef(false);
  const [preview, setPreview] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState('');
  nameTouchedRef.current = nameTouched;

  useEffect(() => {
    if (!open) return;
    setUrlInput(seedUrl || '');
    setName('');
    setNameTouched(false);
    nameTouchedRef.current = false;
    setPreview(null);
    setFetching(false);
    setFetchError('');
  }, [open, seedUrl]);

  useEffect(() => {
    if (!open) return undefined;
    const url = resolveNoteLinkUrl(urlInput);
    if (!url || url.length < 8) {
      setPreview(null);
      setFetching(false);
      setFetchError('');
      return undefined;
    }
    let cancelled = false;
    setFetching(true);
    setFetchError('');
    const timer = window.setTimeout(async () => {
      try {
        const data = await fetchLinkPreview(url);
        if (cancelled) return;
        setPreview(data || null);
        const autoTitle = String(data?.title || data?.siteName || '').trim();
        if (autoTitle && !nameTouchedRef.current) {
          setName(autoTitle);
        }
      } catch {
        if (cancelled) return;
        setPreview(null);
        setFetchError('Could not load a preview. You can still save the link and set a name.');
      } finally {
        if (!cancelled) setFetching(false);
      }
    }, 450);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, urlInput]);

  if (!open) return null;

  const resolvedUrl = resolveNoteLinkUrl(urlInput);
  const canSave = Boolean(resolvedUrl);

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label="Add link"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Add link</h2>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Paste a URL. We fill the name from the page title when we can — you can change it.
        </p>
        <label className="mt-4 block text-xs font-medium text-gray-600 dark:text-gray-300">
          URL
          <input
            value={urlInput}
            onChange={(event) => setUrlInput(event.target.value)}
            placeholder="https://…"
            className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
            autoFocus
          />
        </label>
        <label className="mt-3 block text-xs font-medium text-gray-600 dark:text-gray-300">
          Link name
          <input
            value={name}
            onChange={(event) => {
              setNameTouched(true);
              setName(event.target.value);
            }}
            placeholder={fetching ? 'Loading title…' : 'e.g. Design doc'}
            className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
          />
        </label>
        <div className="mt-3 min-h-[72px]">
          {fetching ? (
            <p className="text-[11px] text-gray-400">Loading preview…</p>
          ) : preview ? (
            <div className="flex gap-2 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-50 dark:bg-gray-800/60">
              {preview.imageUrl ? (
                <img
                  src={preview.imageUrl}
                  alt=""
                  className="w-20 h-16 object-cover flex-shrink-0 bg-gray-200"
                />
              ) : null}
              <div className="min-w-0 flex-1 p-2">
                <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                  {preview.title || name || hostnameFromUrl(resolvedUrl)}
                </p>
                {preview.description ? (
                  <p className="mt-0.5 text-[10px] text-gray-500 dark:text-gray-400 line-clamp-2">
                    {preview.description}
                  </p>
                ) : null}
                <p className="mt-0.5 text-[10px] text-gray-400 truncate">
                  {preview.domain || hostnameFromUrl(resolvedUrl)}
                </p>
              </div>
            </div>
          ) : fetchError ? (
            <p className="text-[11px] text-amber-700 dark:text-amber-300">{fetchError}</p>
          ) : (
            <p className="text-[11px] text-gray-400">Preview appears after you paste a URL.</p>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSave}
            onClick={() => {
              if (!canSave) return;
              const displayName = name.trim()
                || String(preview?.title || '').trim()
                || hostnameFromUrl(resolvedUrl);
              onSave({
                url: resolvedUrl,
                fileName: displayName,
                previewImageUrl: preview?.imageUrl || '',
                previewDescription: preview?.description || '',
                siteName: preview?.siteName || '',
                domain: preview?.domain || hostnameFromUrl(resolvedUrl),
              });
              onClose();
            }}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary-600 text-white disabled:opacity-50"
          >
            Add link
          </button>
        </div>
      </div>
    </div>
  );
};

const stripHtml = (html) =>
  String(html || '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const firstImageSrc = (html) => {
  const match = String(html || '').match(/<img[^>]+src=["']([^"']+)["']/i);
  return match ? match[1] : null;
};

const fileKeyFromSrc = (src) => {
  if (!src) return null;
  try {
    const parsed = new URL(src, window.location.origin);
    const marker = '/files/';
    const idx = parsed.pathname.indexOf(marker);
    if (idx >= 0) {
      return decodeURIComponent(parsed.pathname.slice(idx + marker.length));
    }
  } catch {
    // ignore
  }
  const legacy = String(src).match(/\/files\/([^/?#]+)/);
  return legacy ? decodeURIComponent(legacy[1]) : src;
};

const htmlHasAttachment = (html, item) => {
  const fileUrl = String(item?.fileUrl || '');
  if (!fileUrl) return false;
  const hay = String(html || '');
  if (hay.includes(fileUrl)) return true;
  const key = fileKeyFromSrc(fileUrl);
  return Boolean(key && hay.includes(key));
};

const noteTitle = (note) => {
  const titled = String(note?.title || '').trim();
  if (titled) return titled;
  const snippet = stripHtml(note?.contentHtml);
  return snippet ? snippet.slice(0, 48) : 'Untitled note';
};

const formatUpdated = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const noteTimestampMs = (value) => {
  if (value == null || value === '') return 0;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (Array.isArray(value) && value.length >= 3) {
    const year = Number(value[0]);
    const month = Number(value[1]) - 1;
    const day = Number(value[2]);
    const hour = Number(value[3] || 0);
    const minute = Number(value[4] || 0);
    const second = Number(value[5] || 0);
    const nano = Number(value[6] || 0);
    return new Date(year, month, day, hour, minute, second, Math.floor(nano / 1e6)).getTime();
  }
  const raw = String(value).replace(/(\.\d{3})\d+/, '$1');
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

const noteStampKey = (value) => {
  if (value == null || value === '') return '';
  if (Array.isArray(value)) return value.slice(0, 6).join(',');
  return String(value).replace(/(\.\d{3})\d+/, '$1');
};

const noteTimestampsDiffer = (left, right) => {
  const aKey = noteStampKey(left);
  const bKey = noteStampKey(right);
  if (!aKey || !bKey) return Boolean(aKey || bKey);
  if (aKey === bKey) return false;
  const a = noteTimestampMs(left);
  const b = noteTimestampMs(right);
  if (a && b) return Math.floor(a / 1000) !== Math.floor(b / 1000);
  return true;
};

const normalizeNoteTags = (tags) => {
  const seen = new Set();
  const out = [];
  (Array.isArray(tags) ? tags : []).forEach((raw) => {
    if (out.length >= 24) return;
    const tag = String(raw || '').trim().replace(/^#+/, '').trim().slice(0, 32);
    if (!tag) return;
    const key = tag.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(tag);
  });
  return out;
};

const noteSearchText = (note) => {
  const attachmentText = (note?.attachments || [])
    .map((item) => [item.fileName, item.linkUrl, item.domain, item.fileType].filter(Boolean).join(' '))
    .join(' ');
  const sensitiveText = (note?.sensitiveBlocks || [])
    .map((block) => [
      block.title || block.name,
      ...((block.entries || []).map((entry) => entry.key)),
    ].filter(Boolean).join(' '))
    .join(' ');
  return [
    noteTitle(note),
    stripHtml(note?.contentHtml),
    attachmentText,
    sensitiveText,
    normalizeNoteTags(note?.tags).join(' '),
  ].join('\n').toLowerCase();
};

const noteHasTag = (note, tag) => {
  const needle = String(tag || '').trim().toLowerCase();
  if (!needle) return true;
  return normalizeNoteTags(note?.tags).some((item) => item.toLowerCase() === needle);
};

const TAG_PALETTE = [
  { gradient: 'linear-gradient(135deg, rgba(191,219,254,0.7), rgba(221,214,254,0.7))', text: '#6366f1', ring: 'rgba(165,180,252,0.9)' },
  { gradient: 'linear-gradient(135deg, rgba(167,243,208,0.7), rgba(165,243,252,0.7))', text: '#14b8a6', ring: 'rgba(94,234,212,0.9)' },
  { gradient: 'linear-gradient(135deg, rgba(254,205,211,0.7), rgba(254,215,170,0.7))', text: '#fb7185', ring: 'rgba(253,186,116,0.9)' },
  { gradient: 'linear-gradient(135deg, rgba(253,230,138,0.7), rgba(254,215,170,0.7))', text: '#f59e0b', ring: 'rgba(252,211,77,0.9)' },
  { gradient: 'linear-gradient(135deg, rgba(221,214,254,0.7), rgba(251,207,232,0.7))', text: '#a78bfa', ring: 'rgba(216,180,254,0.9)' },
  { gradient: 'linear-gradient(135deg, rgba(165,243,252,0.7), rgba(191,219,254,0.7))', text: '#22d3ee', ring: 'rgba(103,232,249,0.9)' },
  { gradient: 'linear-gradient(135deg, rgba(251,207,232,0.7), rgba(254,205,211,0.7))', text: '#f472b6', ring: 'rgba(249,168,212,0.9)' },
  { gradient: 'linear-gradient(135deg, rgba(254,215,170,0.7), rgba(253,230,138,0.7))', text: '#fb923c', ring: 'rgba(253,186,116,0.9)' },
  { gradient: 'linear-gradient(135deg, rgba(153,246,228,0.7), rgba(167,243,208,0.7))', text: '#2dd4bf', ring: 'rgba(94,234,212,0.9)' },
  { gradient: 'linear-gradient(135deg, rgba(199,210,254,0.7), rgba(191,219,254,0.7))', text: '#818cf8', ring: 'rgba(165,180,252,0.9)' },
  { gradient: 'linear-gradient(135deg, rgba(217,249,157,0.7), rgba(187,247,208,0.7))', text: '#84cc16', ring: 'rgba(190,242,100,0.9)' },
  { gradient: 'linear-gradient(135deg, rgba(186,230,253,0.7), rgba(165,243,252,0.7))', text: '#38bdf8', ring: 'rgba(125,211,252,0.9)' },
];

const BUILTIN_TAGS = ['training', 'credential', 'important', 'command', 'bank', 'work', 'meeting', 'personal', 'idea'];

const TAG_ICON_RULES = [
  { kind: 'training', keywords: ['training', 'train', 'teacher', 'course', 'learn', 'class', 'education', 'interview'] },
  { kind: 'bank', keywords: ['bank', 'banking', 'finance', 'gst', 'account', 'invoice', 'payment', 'money'] },
  { kind: 'credential', keywords: ['credential', 'credentials', 'password', 'secret', 'login', 'auth', 'pin'] },
  { kind: 'important', keywords: ['important', 'urgent', 'priority', 'critical', 'star'] },
  { kind: 'command', keywords: ['command', 'cmd', 'terminal', 'script', 'cli', 'npm', 'mvn'] },
  { kind: 'work', keywords: ['work', 'office', 'job', 'task', 'workflow'] },
  { kind: 'meeting', keywords: ['meeting', 'call', 'standup'] },
  { kind: 'personal', keywords: ['personal', 'home', 'family'] },
  { kind: 'idea', keywords: ['idea', 'todo', 'plan'] },
];

const tagKind = (tag) => {
  const value = String(tag || '').toLowerCase();
  const match = TAG_ICON_RULES.find((rule) => rule.keywords.some((word) => value.includes(word)));
  return match?.kind || 'tag';
};

const TagIcon = ({ tag, className = 'h-3 w-3' }) => {
  const kind = tagKind(tag);
  const paths = {
    training: 'M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5',
    bank: 'M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.425 48.425 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z',
    credential: 'M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z',
    important: 'M12 3.75l2.09 6.26H21l-5.17 3.76 1.97 6.23L12 16.5l-5.8 3.5 1.97-6.23L3 10.01h6.91L12 3.75z',
    command: 'M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25z',
    work: 'M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0',
    meeting: 'M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5',
    personal: 'M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25',
    idea: 'M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18',
    tag: 'M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z M6 6h.008v.008H6V6z',
  };
  return (
    <svg className={`flex-shrink-0 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={paths[kind] || paths.tag} />
    </svg>
  );
};

const tagColor = (tag) => {
  const value = String(tag || '').toLowerCase();
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return TAG_PALETTE[hash % TAG_PALETTE.length];
};

const collectTagSuggestions = (draft, currentTags, usedTags) => {
  const taken = new Set(normalizeNoteTags(currentTags).map((tag) => tag.toLowerCase()));
  const query = String(draft || '').trim().toLowerCase();
  const seen = new Set();
  const out = [];
  [...BUILTIN_TAGS, ...(Array.isArray(usedTags) ? usedTags : [])].forEach((raw) => {
    const tag = String(raw || '').trim();
    const key = tag.toLowerCase();
    if (!tag || taken.has(key) || seen.has(key)) return;
    if (query && !key.includes(query)) return;
    seen.add(key);
    out.push(tag);
  });
  return out.slice(0, 10);
};

const NoteTagAddControl = ({
  adding,
  draft,
  currentTags,
  usedTags,
  onDraftChange,
  onStartAdd,
  onCommitAdd,
  onCancelAdd,
  onPickSuggestion,
  className = '',
}) => {
  const suggestions = collectTagSuggestions(draft, currentTags, usedTags);
  if (!adding) {
    return (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onStartAdd();
        }}
        className={`inline-flex items-center rounded-full border border-dashed border-gray-400/70 bg-white/40 px-2 py-0.5 text-[11px] font-semibold text-gray-600 opacity-50 hover:opacity-100 dark:border-gray-500 dark:bg-gray-900/40 dark:text-gray-300 ${className}`}
      >
        + tag
      </button>
    );
  }
  return (
    <div className="relative">
      <input
        autoFocus
        value={draft}
        maxLength={32}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => onDraftChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            if (suggestions[0] && !String(draft || '').trim()) {
              onPickSuggestion(suggestions[0]);
              return;
            }
            onCommitAdd();
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            onCancelAdd();
          }
        }}
        onBlur={onCommitAdd}
        placeholder="Tag name"
        className={`w-28 rounded-full border border-primary-300 bg-white/80 px-2 py-0.5 text-[11px] text-gray-800 outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-primary-700 dark:bg-gray-900/80 dark:text-gray-100 ${className}`}
      />
      {suggestions.length > 0 ? (
        <div className="absolute bottom-full right-0 z-30 mb-1 w-44 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900">
          {suggestions.map((tag) => (
            <button
              key={tag.toLowerCase()}
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onPickSuggestion(tag);
              }}
              className="flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left text-[11px] font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              <TagIcon tag={tag} />
              <span className="min-w-0 truncate">{tag}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const NoteTagChip = ({ tag, selected, onSelectTag, onRemoveTag }) => {
  const color = tagColor(tag);
  return (
    <span
      className={`inline-flex max-w-full items-center gap-1 rounded-full border-0 pl-1.5 pr-1 py-0.5 text-[11px] font-semibold ${
        selected ? 'ring-2 ring-offset-1' : ''
      }`}
      style={{
        backgroundImage: color.gradient,
        color: color.text,
        boxShadow: selected ? `0 0 0 1px ${color.ring}` : undefined,
      }}
    >
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onSelectTag?.(tag);
        }}
        className="inline-flex min-w-0 items-center gap-1 truncate"
        title={`Show notes tagged ${tag}`}
      >
        <TagIcon tag={tag} />
        <span className="min-w-0 truncate">{tag}</span>
      </button>
      {onRemoveTag ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onRemoveTag(tag);
          }}
          className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-current/70 hover:bg-white/40"
          aria-label={`Remove tag ${tag}`}
        >
          ×
        </button>
      ) : null}
    </span>
  );
};

const NoteTagChips = ({
  tags,
  activeTag,
  adding,
  draft,
  usedTags,
  onDraftChange,
  onStartAdd,
  onCommitAdd,
  onCancelAdd,
  onPickSuggestion,
  onSelectTag,
  onRemoveTag,
  addPosition = 'inline',
}) => {
  const chips = normalizeNoteTags(tags).map((tag) => (
    <NoteTagChip
      key={tag.toLowerCase()}
      tag={tag}
      selected={String(activeTag || '').toLowerCase() === tag.toLowerCase()}
      onSelectTag={onSelectTag}
      onRemoveTag={onRemoveTag}
    />
  ));
  const addControl = (
    <NoteTagAddControl
      adding={adding}
      draft={draft}
      currentTags={tags}
      usedTags={usedTags}
      onDraftChange={onDraftChange}
      onStartAdd={onStartAdd}
      onCommitAdd={onCommitAdd}
      onCancelAdd={onCancelAdd}
      onPickSuggestion={onPickSuggestion}
    />
  );
  if (addPosition === 'bottomRight') {
    return (
      <div className={`relative min-h-[28px] pr-14 ${adding ? 'z-20' : ''}`}>
        <div className="flex flex-wrap items-center gap-1.5">
          {chips}
        </div>
        <div className="absolute bottom-0 right-0">
          {addControl}
        </div>
      </div>
    );
  }
  return (
    <div className={`relative flex flex-wrap items-center gap-1.5 ${adding ? 'z-20' : ''}`}>
      {chips}
      {addControl}
    </div>
  );
};

const countQueryMatches = (haystack, query) => {
  const text = String(haystack || '').toLowerCase();
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return 0;
  let count = 0;
  let from = 0;
  while (from <= text.length) {
    const at = text.indexOf(needle, from);
    if (at < 0) break;
    count += 1;
    from = at + Math.max(needle.length, 1);
  }
  return count;
};

const highlightPlainText = (text, query) => {
  const raw = String(text || '');
  const needle = String(query || '').trim();
  if (!needle || !raw) return raw;
  const lower = raw.toLowerCase();
  const q = needle.toLowerCase();
  const nodes = [];
  let from = 0;
  let key = 0;
  while (from < raw.length) {
    const at = lower.indexOf(q, from);
    if (at < 0) {
      nodes.push(raw.slice(from));
      break;
    }
    if (at > from) nodes.push(raw.slice(from, at));
    nodes.push(
      <mark
        key={`h-${key}`}
        className="rounded-sm bg-amber-200 text-gray-900 dark:bg-amber-400/70 dark:text-gray-900 px-0.5"
      >
        {raw.slice(at, at + needle.length)}
      </mark>
    );
    key += 1;
    from = at + needle.length;
  }
  return nodes;
};

const collectTextMatchRanges = (root, query) => {
  const ranges = [];
  const needle = String(query || '').trim();
  if (!root || !needle) return ranges;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const q = needle.toLowerCase();
  let node = walker.nextNode();
  while (node) {
    const value = node.nodeValue || '';
    const lower = value.toLowerCase();
    let from = 0;
    while (from < value.length) {
      const at = lower.indexOf(q, from);
      if (at < 0) break;
      try {
        const range = document.createRange();
        range.setStart(node, at);
        range.setEnd(node, Math.min(value.length, at + needle.length));
        ranges.push(range);
      } catch {
        // ignore invalid range
      }
      from = at + Math.max(needle.length, 1);
    }
    node = walker.nextNode();
  }
  return ranges;
};

const clearNoteSearchHighlights = () => {
  if (!CSS.highlights) return;
  CSS.highlights.delete('note-search-hit');
  CSS.highlights.delete('note-search-current');
};

const applyNoteSearchHighlights = (root, query, currentOrdinal) => {
  clearNoteSearchHighlights();
  const HighlightCtor = typeof window !== 'undefined' ? window.Highlight : undefined;
  if (!CSS.highlights || typeof HighlightCtor !== 'function') return;
  const ranges = collectTextMatchRanges(root, query);
  if (!ranges.length) return;
  const currentIdx = Math.max(0, (currentOrdinal || 1) - 1);
  const current = ranges[currentIdx] || ranges[0];
  const others = ranges.filter((range) => range !== current);
  if (others.length) {
    CSS.highlights.set('note-search-hit', new HighlightCtor(...others));
  }
  if (current) {
    CSS.highlights.set('note-search-current', new HighlightCtor(current));
  }
};

const NotesSearchBox = ({
  value,
  onChange,
  placeholder,
  onKeyDown,
  onFocus,
  onBlur,
  suffix,
  compact,
}) => (
  <div className="relative min-w-0 flex-1">
    <svg
      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z" />
    </svg>
    <input
      type="search"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      placeholder={placeholder}
      className={`w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 pl-9 ${suffix ? 'pr-24' : 'pr-3'} ${compact ? 'py-1.5 text-[12px]' : 'py-2 text-sm'} text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-300`}
    />
    {suffix ? (
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
        {suffix}
      </div>
    ) : null}
  </div>
);

const NotesHomePanel = ({ onClose }) => {
  const dispatch = useDispatch();
  const currentUserId = useSelector((state) => state.auth?.user?.id);
  const preferredLanguage = useSelector((state) => state.auth?.user?.preferredLanguage || 'en');
  const users = useSelector((state) => state.users?.users || []);
  const quillRef = useRef(null);
  const saveTimerRef = useRef(null);
  const pendingPayloadRef = useRef(null);
  const selectedIdRef = useRef(null);
  const titleRef = useRef('');
  const contentHtmlRef = useRef('');
  const originalContentHtmlRef = useRef('');
  const viewingOriginalRef = useRef(false);
  const attachmentsRef = useRef([]);
  const sensitiveBlocksRef = useRef([]);
  const tagsRef = useRef([]);
  const shareMetaRef = useRef(null);
  const lastSyncedRef = useRef(null);
  const refreshingLiveRef = useRef(false);
  const saveStateRef = useRef('saved');
  const liveCheckBusyRef = useRef(false);
  const liveCheckAtRef = useRef(0);
  const loadLiveNoteRef = useRef(async () => {});

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  const [shareMeta, setShareMeta] = useState(null);
  const [refreshingLive, setRefreshingLive] = useState(false);
  const [liveNoteStale, setLiveNoteStale] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [title, setTitle] = useState('');
  const [contentHtml, setContentHtml] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sensitiveBlocks, setSensitiveBlocks] = useState([]);
  const [tags, setTags] = useState([]);
  const [listTagFilter, setListTagFilter] = useState('');
  const [addingTagForId, setAddingTagForId] = useState(null);
  const [tagDraft, setTagDraft] = useState('');
  const [saveState, setSaveState] = useState('saved');
  const [viewerIndex, setViewerIndex] = useState(null);
  const [viewerItems, setViewerItems] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [secretModalOpen, setSecretModalOpen] = useState(false);
  const [secretSeedText, setSecretSeedText] = useState('');
  const [editingSensitiveBlock, setEditingSensitiveBlock] = useState(null);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkSeedUrl, setLinkSeedUrl] = useState('');
  const [copiedSecretId, setCopiedSecretId] = useState(null);
  const [revealSecret, setRevealSecret] = useState(null);
  const [expandedSensitiveId, setExpandedSensitiveId] = useState(null);
  const [editingAttachmentId, setEditingAttachmentId] = useState(null);
  const [attachDragOver, setAttachDragOver] = useState(false);
  const [listSearch, setListSearch] = useState('');
  const [credentialsView, setCredentialsView] = useState(false);
  const [captionsView, setCaptionsView] = useState(false);
  const [credentialSearch, setCredentialSearch] = useState('');
  const [credentialBlocks, setCredentialBlocks] = useState([]);
  const [hydratedNotes, setHydratedNotes] = useState({});
  const [expandedVaultKey, setExpandedVaultKey] = useState(null);
  const [noteSearch, setNoteSearch] = useState('');
  const [noteSearchOpen, setNoteSearchOpen] = useState(false);
  const [noteMatchIndex, setNoteMatchIndex] = useState(0);
  const [pinEnabled, setPinEnabled] = useState(false);
  const [pinModal, setPinModal] = useState(null);
  const [taskComposerOpen, setTaskComposerOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiCustomOpen, setAiCustomOpen] = useState(false);
  const [aiProgress, setAiProgress] = useState(0);
  const [viewingOriginal, setViewingOriginal] = useState(false);
  const [callPeople, setCallPeople] = useState([]);
  const [buyingItems, setBuyingItems] = useState([]);
  const [taskItems, setTaskItems] = useState([]);
  const [taskSeedTitle, setTaskSeedTitle] = useState('');
  const pendingTaskTitlesRef = useRef([]);
  const [callReminderOpen, setCallReminderOpen] = useState(false);
  const [callReminderSaving, setCallReminderSaving] = useState(false);
  const attachInputRef = useRef(null);
  const pendingPinActionRef = useRef(null);
  const credentialBlocksRef = useRef([]);
  const vaultEditItemRef = useRef(null);

  selectedIdRef.current = selectedId;
  titleRef.current = title;
  attachmentsRef.current = attachments;
  sensitiveBlocksRef.current = sensitiveBlocks;
  tagsRef.current = tags;
  shareMetaRef.current = shareMeta;
  credentialBlocksRef.current = credentialBlocks;
  refreshingLiveRef.current = refreshingLive;
  saveStateRef.current = saveState;

  const loadCredentials = useCallback(async () => {
    try {
      const items = await fetchCredentialBucket();
      setCredentialBlocks(normalizeSensitiveBlocks(items));
    } catch {
      setCredentialBlocks([]);
    }
  }, []);

  const ensureHydratedNote = async (noteId) => {
    const cached = hydratedNotes[noteId];
    const hasValues = normalizeSensitiveBlocks(cached?.sensitiveBlocks).some((block) =>
      (block.entries || []).some((entry) => entry.value),
    );
    if (hasValues) return cached;
    const full = await fetchNote(noteId);
    setHydratedNotes((prev) => ({ ...prev, [noteId]: full }));
    return full;
  };

  const persistCredentialBlocks = useCallback(async (next) => {
    const saved = await saveCredentialBucket(next);
    const normalized = normalizeSensitiveBlocks(saved);
    credentialBlocksRef.current = normalized;
    setCredentialBlocks(normalized);
    return normalized;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchMyNotes();
      setNotes(Array.isArray(data) ? data : []);
      await loadCredentials();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [loadCredentials]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onNotesChanged = () => {
      load();
    };
    window.addEventListener('workloop:notes-changed', onNotesChanged);
    return () => window.removeEventListener('workloop:notes-changed', onNotesChanged);
  }, [load]);

  useEffect(() => {
    const onCallReminders = () => setSelectedId(null);
    window.addEventListener('workloop:open-call-reminders', onCallReminders);
    return () => window.removeEventListener('workloop:open-call-reminders', onCallReminders);
  }, []);

  useEffect(() => {
    if (!aiBusy) {
      setAiProgress(0);
      return undefined;
    }
    setAiProgress(12);
    const timer = window.setInterval(() => {
      setAiProgress((prev) => (prev >= 92 ? 92 : prev + Math.max(1, Math.round((92 - prev) * 0.12))));
    }, 220);
    return () => window.clearInterval(timer);
  }, [aiBusy]);

  useEffect(() => {
    let cancelled = false;
    fetchCredentialPinStatus()
      .then((data) => {
        if (!cancelled) setPinEnabled(Boolean(data?.enabled));
      })
      .catch(() => {
        if (!cancelled) setPinEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const applyNote = useCallback((note) => {
    setSelectedId(note?.id || null);
    setShareMeta(note?.id ? {
      shareKind: String(note.shareKind || '').toUpperCase(),
      sharedByName: note.sharedByName || '',
      userId: note.userId || null,
      sharedMemberIds: Array.isArray(note.sharedMemberIds) ? note.sharedMemberIds : [],
    } : null);
    setTitle(note?.title || '');
    contentHtmlRef.current = note?.contentHtml || '';
    originalContentHtmlRef.current = note?.originalContentHtml || '';
    viewingOriginalRef.current = false;
    setViewingOriginal(false);
    setContentHtml(note?.contentHtml || '');
    setAttachments(Array.isArray(note?.attachments) ? note.attachments : []);
    setSensitiveBlocks(normalizeSensitiveBlocks(note?.sensitiveBlocks));
    setTags(normalizeNoteTags(note?.tags));
    tagsRef.current = normalizeNoteTags(note?.tags);
    setSaveState('saved');
    setViewerIndex(null);
    setViewerItems(null);
    setSecretModalOpen(false);
    setSecretSeedText('');
    setEditingSensitiveBlock(null);
    setLinkModalOpen(false);
    setLinkSeedUrl('');
    setCopiedSecretId(null);
    setExpandedSensitiveId(null);
    setEditingAttachmentId(null);
    setNoteSearch('');
    setNoteSearchOpen(false);
    setNoteMatchIndex(0);
    setAiPrompt('');
    setAiBusy(false);
    setAiCustomOpen(false);
    setAiProgress(0);
    if (!note?.id) setTaskComposerOpen(false);
    lastSyncedRef.current = note?.id ? snapshotNoteFields(note) : null;
    setLiveNoteStale(false);
  }, []);

  const persistNow = useCallback(async (noteId, payload) => {
    if (!noteId) return null;
    const saved = await updateNote(noteId, payload);
    setNotes((prev) => prev.map((item) => (item.id === saved.id ? redactNoteForList(saved) : item)));
    if (selectedIdRef.current === saved.id) {
      setShareMeta({
        shareKind: String(saved.shareKind || '').toUpperCase(),
        sharedByName: saved.sharedByName || '',
        userId: saved.userId || null,
        sharedMemberIds: Array.isArray(saved.sharedMemberIds) ? saved.sharedMemberIds : [],
      });
      lastSyncedRef.current = snapshotNoteFields(saved);
      setLiveNoteStale(false);
    }
    return saved;
  }, []);

  const checkLiveNoteTimestamp = useCallback(async () => {
    const noteId = selectedIdRef.current;
    const live = String(shareMetaRef.current?.shareKind || '').toUpperCase() === 'LIVE';
    if (!live || !noteId || refreshingLiveRef.current || liveCheckBusyRef.current) return;
    const now = Date.now();
    if (now - liveCheckAtRef.current < 1200) return;
    liveCheckAtRef.current = now;
    liveCheckBusyRef.current = true;
    try {
      const remote = await fetchNote(noteId);
      if (selectedIdRef.current !== noteId) return;
      const synced = lastSyncedRef.current?.id === noteId ? lastSyncedRef.current : null;
      const timeChanged = noteTimestampsDiffer(synced?.updatedAt, remote?.updatedAt);
      const bodyChanged = synced
        ? !sameNoteText(synced.contentHtml, remote?.contentHtml)
          || !sameNoteText(synced.title, remote?.title)
        : false;
      const stale = Boolean(timeChanged || bodyChanged);
      setLiveNoteStale(stale);
      if (stale) {
        liveCheckBusyRef.current = false;
        await loadLiveNoteRef.current(remote, { notify: true });
        setLiveNoteStale(true);
      }
    } catch {
      // ignore
    } finally {
      liveCheckBusyRef.current = false;
    }
  }, []);

  const currentNotePayload = useCallback(() => ({
    title: titleRef.current,
    contentHtml: contentHtmlRef.current,
    originalContentHtml: originalContentHtmlRef.current,
    attachments: attachmentsRef.current,
    sensitiveBlocks: sensitiveBlocksRef.current,
    tags: tagsRef.current,
  }), []);

  const applyTagsToNote = useCallback((noteId, nextTags) => {
    const tagsNext = normalizeNoteTags(nextTags);
    setNotes((prev) => prev.map((item) => (item.id === noteId ? { ...item, tags: tagsNext } : item)));
    if (selectedIdRef.current === noteId) {
      tagsRef.current = tagsNext;
      setTags(tagsNext);
    }
    return tagsNext;
  }, []);

  const persistNoteTags = useCallback(async (noteId, nextTags) => {
    if (!noteId) return;
    const tagsNext = applyTagsToNote(noteId, nextTags);
    try {
      const payload = selectedIdRef.current === noteId
        ? { ...currentNotePayload(), tags: tagsNext }
        : { tags: tagsNext };
      await persistNow(noteId, payload);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save tags');
    }
  }, [applyTagsToNote, currentNotePayload, persistNow]);

  const commitTagDraft = useCallback((noteId, currentTags) => {
    const next = String(tagDraft || '').trim();
    setAddingTagForId(null);
    setTagDraft('');
    if (!next || !noteId) return;
    persistNoteTags(noteId, [...normalizeNoteTags(currentTags), next]);
  }, [persistNoteTags, tagDraft]);

  const pickSuggestedTag = useCallback((noteId, currentTags, tag) => {
    setAddingTagForId(null);
    setTagDraft('');
    if (!noteId || !tag) return;
    persistNoteTags(noteId, [...normalizeNoteTags(currentTags), tag]);
  }, [persistNoteTags]);

  const removeNoteTag = useCallback((noteId, currentTags, tag) => {
    const key = String(tag || '').toLowerCase();
    persistNoteTags(
      noteId,
      normalizeNoteTags(currentTags).filter((item) => item.toLowerCase() !== key),
    );
    if (listTagFilter && listTagFilter.toLowerCase() === key) {
      const stillUsed = notes.some((note) => note.id !== noteId && noteHasTag(note, tag));
      if (!stillUsed) setListTagFilter('');
    }
  }, [listTagFilter, notes, persistNoteTags]);

  const toggleTagFilter = useCallback((tag) => {
    const next = String(tag || '').trim();
    setListTagFilter((prev) => (prev && prev.toLowerCase() === next.toLowerCase() ? '' : next));
  }, []);

  const scheduleSave = useCallback((patch) => {
    const noteId = selectedIdRef.current;
    if (!noteId) return;
    pendingPayloadRef.current = {
      ...currentNotePayload(),
      ...patch,
    };
    setSaveState((prev) => (prev === 'unsaved' ? prev : 'unsaved'));
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = window.setTimeout(async () => {
      const payload = pendingPayloadRef.current;
      pendingPayloadRef.current = null;
      try {
        setSaveState((prev) => (prev === 'error' ? prev : 'saving'));
        await persistNow(noteId, payload);
        if (selectedIdRef.current === noteId && !pendingPayloadRef.current) {
          setSaveState('saved');
        }
      } catch (err) {
        setSaveState('error');
        setError(err?.response?.data?.error || err?.message || 'Failed to save note');
      }
    }, AUTOSAVE_MS);
  }, [currentNotePayload, persistNow]);

  useEffect(() => () => {
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
    }
    const noteId = selectedIdRef.current;
    const payload = pendingPayloadRef.current;
    if (noteId && payload) {
      updateNote(noteId, payload).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (viewerIndex == null && !secretModalOpen && !linkModalOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.stopImmediatePropagation();
      if (viewerIndex != null) {
        setViewerIndex(null);
        setViewerItems(null);
      }
      else if (secretModalOpen) {
        setSecretModalOpen(false);
        setSecretSeedText('');
        setEditingSensitiveBlock(null);
      }
      else {
        setLinkModalOpen(false);
        setLinkSeedUrl('');
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [viewerIndex, secretModalOpen, linkModalOpen]);

  const flushSave = useCallback(async () => {
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const noteId = selectedIdRef.current;
    const payload = pendingPayloadRef.current;
    if (!noteId || !payload) return;
    pendingPayloadRef.current = null;
    try {
      await persistNow(noteId, payload);
      setSaveState('saved');
    } catch {
      setSaveState('error');
    }
  }, [persistNow]);

  const openNote = async (note, { startTask = false } = {}) => {
    await flushSave();
    setCredentialsView(false);
    if (!note?.id) {
      applyNote(null);
      return;
    }
    try {
      const full = await fetchNote(note.id);
      applyNote(full);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to open note');
      applyNote(note);
    }
    setTaskComposerOpen(Boolean(startTask));
  };

  const openTaskComposer = async (note) => {
    if (note?.id && note.id !== selectedIdRef.current) {
      await openNote(note, { startTask: true });
      return;
    }
    setTaskComposerOpen(true);
  };

  const handleNewNote = async () => {
    await flushSave();
    setCredentialsView(false);
    setCaptionsView(false);
    setError('');
    setTaskComposerOpen(false);
    try {
      const created = await createNote({ title: '', contentHtml: '', attachments: [], sensitiveBlocks: [], tags: [] });
      setNotes((prev) => [redactNoteForList(created), ...prev.filter((item) => item.id !== created.id)]);
      applyNote(created);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to create note');
    }
  };

  const handleBackToList = async () => {
    await flushSave();
    applyNote(null);
    setCredentialsView(false);
    setCredentialSearch('');
    setExpandedVaultKey(null);
  };

  const openCredentialsView = async () => {
    await flushSave();
    applyNote(null);
    setCaptionsView(false);
    setCredentialsView(true);
    setExpandedVaultKey(null);
    await loadCredentials();
  };

  const requestDeleteNote = (note, event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const id = note?.id || note?._id || selectedId;
    if (!id) return;
    setDeleteTarget({
      id,
      title: noteTitle({ ...note, title: note?.title || title, contentHtml: note?.contentHtml || contentHtml }),
      userId: note?.userId,
      shareKind: String(note?.shareKind || '').toUpperCase(),
    });
  };

  const confirmDeleteNote = async () => {
    const noteId = deleteTarget?.id;
    if (!noteId) return;
    setDeleteTarget(null);
    setTaskComposerOpen(false);
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    pendingPayloadRef.current = null;
    try {
      await deleteNote(noteId);
      setNotes((prev) => prev.filter((item) => item.id !== noteId && item._id !== noteId));
      if (selectedIdRef.current === noteId) applyNote(null);
      window.dispatchEvent(new CustomEvent('workloop:notes-changed'));
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to delete note');
    }
  };

  const handleDelete = () => {
    requestDeleteNote({
      id: selectedId,
      title,
      userId: shareMeta?.userId,
      shareKind: shareMeta?.shareKind,
    });
  };

  const openShareNote = (note, event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const target = note?.id
      ? note
      : {
        id: selectedId,
        title,
        userId: shareMeta?.userId,
        shareKind: shareMeta?.shareKind,
      };
    if (!target?.id) return;
    setShareTarget(target);
  };

  const otherLivePersonName = (note, people) => {
    const ids = [note?.userId, ...(Array.isArray(note?.sharedMemberIds) ? note.sharedMemberIds : [])]
      .filter(Boolean);
    const otherId = ids.find((id) => id !== currentUserId);
    const person = (people || []).find((user) => user?.id === otherId);
    const named = personDisplayName(person);
    if (named) return named;
    if (note?.userId && note.userId !== currentUserId && note.sharedByName) {
      return note.sharedByName;
    }
    return 'Someone';
  };

  const paintNoteHtml = (html) => {
    const next = toQuillCompatibleHtml(html || '') || html || '<p><br></p>';
    contentHtmlRef.current = next;
    setContentHtml(next);
    const editor = quillRef.current?.getEditor?.();
    if (editor) {
      const applied = setQuillHtml(editor, next);
      if (!applied && editor.root) {
        editor.root.innerHTML = next;
      }
    }
    return next;
  };

  const loadLiveNote = async (prefetched, { notify = false } = {}) => {
    const noteId = selectedIdRef.current;
    if (!noteId || refreshingLiveRef.current) return false;
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    pendingPayloadRef.current = null;
    setRefreshingLive(true);
    setError('');
    let loadedRemote = false;
    try {
      let people = users || [];
      if (!people.length) {
        try {
          const result = await dispatch(fetchUsers());
          if (Array.isArray(result?.payload)) people = result.payload;
        } catch {
          people = users || [];
        }
      }
      const localSnap = {
        id: noteId,
        title: titleRef.current,
        contentHtml: quillRef.current?.getEditor?.()?.root?.innerHTML || contentHtmlRef.current,
        attachments: attachmentsRef.current,
        tags: tagsRef.current,
        sensitiveBlocks: sensitiveBlocksRef.current,
      };
      const full = prefetched || await fetchNote(noteId);
      const remoteSnap = snapshotNoteFields(full);
      const base = lastSyncedRef.current?.id === noteId
        ? lastSyncedRef.current
        : remoteSnap;
      const localChanged = liveFieldsChanged(localSnap, base);
      const remoteChanged = liveFieldsChanged(remoteSnap, base);
      const otherName = otherLivePersonName(full, people);
      loadedRemote = remoteChanged;

      if (!remoteChanged) {
        setNotes((prev) => prev.map((item) => (item.id === full.id ? redactNoteForList(full) : item)));
        setSaveState(localChanged ? 'unsaved' : 'saved');
        setLiveNoteStale(false);
        return false;
      }

      if (!localChanged) {
        applyNote(full);
        paintNoteHtml(full.contentHtml);
        setNotes((prev) => prev.map((item) => (item.id === full.id ? redactNoteForList(full) : item)));
      } else {
        const mergedHtml = mergeLiveHtml(
          base.contentHtml,
          localSnap.contentHtml,
          remoteSnap.contentHtml,
          otherName,
        );
        const mergedTitle = sameNoteText(localSnap.title, base.title)
          ? (remoteSnap.title || '')
          : (localSnap.title || '');
        const merged = {
          ...full,
          title: mergedTitle,
          contentHtml: mergedHtml,
          attachments: unionNoteItems(
            localSnap.attachments,
            remoteSnap.attachments,
            (item) => item?.id || item?.fileUrl || item?.linkUrl || item?.fileName,
          ),
          tags: unionNoteItems(localSnap.tags, remoteSnap.tags, (tag) => String(tag || '').toLowerCase()),
          sensitiveBlocks: unionNoteItems(
            localSnap.sensitiveBlocks,
            remoteSnap.sensitiveBlocks,
            (block) => block?.id || String(block?.title || '').toLowerCase(),
          ),
        };
        applyNote(merged);
        const painted = paintNoteHtml(merged.contentHtml);
        setNotes((prev) => prev.map((item) => (item.id === merged.id ? redactNoteForList(merged) : item)));
        await persistNow(noteId, {
          title: merged.title,
          contentHtml: painted,
          attachments: merged.attachments,
          tags: merged.tags,
          sensitiveBlocks: merged.sensitiveBlocks,
        });
      }
      if (notify && loadedRemote) setLiveNoteStale(true);
      return loadedRemote;
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to refresh note');
      return false;
    } finally {
      setRefreshingLive(false);
    }
  };
  loadLiveNoteRef.current = loadLiveNote;

  const handleLiveRefresh = async () => {
    await loadLiveNote();
  };

  const handleTitleChange = (value) => {
    titleRef.current = value;
    setTitle(value);
    scheduleSave({ title: value });
  };

  const scanNoteActions = useCallback((html) => {
    const buying = extractBuyingItemsFromNoteHtml(html);
    setBuyingItems(buying);
    setCallPeople(extractNeedToCallNamesFromNoteHtml(html));
    setTaskItems(extractTaskItemsFromNoteHtml(html, buying));
  }, []);

  const handleHtmlChange = useCallback((html) => {
    if (viewingOriginalRef.current) {
      originalContentHtmlRef.current = html;
      return;
    }
    contentHtmlRef.current = html;
    scanNoteActions(html);
    const prevAttachments = attachmentsRef.current;
    const nextAttachments = prevAttachments.filter(
      (item) => !isDisplayableImage(item) || htmlHasAttachment(html, item),
    );
    if (nextAttachments.length !== prevAttachments.length) {
      attachmentsRef.current = nextAttachments;
      setAttachments(nextAttachments);
      scheduleSave({ contentHtml: html, attachments: nextAttachments });
      return;
    }
    scheduleSave({ contentHtml: html });
  }, [scheduleSave, scanNoteActions]);

  const applyAiHtml = useCallback((html, sourceHtml) => {
    const next = toQuillCompatibleHtml(html);
    if (!next) {
      setError('AI returned an empty result. Try Structure data again.');
      return false;
    }
    const editor = quillRef.current?.getEditor?.();
    const applied = setQuillHtml(editor, next) || next;
    const empty = !String(applied)
      .replace(/<p><br\s*\/?><\/p>/gi, '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (empty) {
      setError('Could not load the structured note into the editor.');
      return false;
    }
    if (!String(originalContentHtmlRef.current || '').trim() && String(sourceHtml || '').trim()) {
      originalContentHtmlRef.current = sourceHtml;
    }
    viewingOriginalRef.current = false;
    setViewingOriginal(false);
    contentHtmlRef.current = applied;
    setContentHtml(applied);
    scheduleSave({
      contentHtml: applied,
      originalContentHtml: originalContentHtmlRef.current,
    });
    return true;
  }, [scheduleSave]);

  const runNoteAi = useCallback(async (requestedMode) => {
    if (aiBusy) return;
    const prompt = String(aiPrompt || '').trim();
    const editorHtml = quillRef.current?.getEditor?.()?.root?.innerHTML || contentHtmlRef.current;
    const noteText = String(editorHtml || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    const mode = requestedMode === 'PROMPT' && !prompt ? 'STRUCTURE' : requestedMode;
    if (!noteText && !prompt && !String(titleRef.current || '').trim()) {
      setError('Type the items in the note, then click Structure data.');
      return;
    }
    setAiBusy(true);
    setError('');
    try {
      const result = await rewriteNoteWithAi({
        mode,
        title: titleRef.current,
        contentHtml: editorHtml,
        prompt,
        language: preferredLanguage,
      });
      const ok = applyAiHtml(result?.contentHtml, editorHtml);
      if (ok) {
        setAiProgress(100);
        if (mode === 'PROMPT') {
          setAiPrompt('');
          setAiCustomOpen(false);
        }
        const structuredHtml = quillRef.current?.getEditor?.()?.root?.innerHTML
          || contentHtmlRef.current
          || result?.contentHtml
          || '';
        const found = extractNeedToCallNamesFromNoteHtml(structuredHtml);
        scanNoteActions(structuredHtml);
        if (mode === 'STRUCTURE' && found.length) {
          setCallReminderOpen(true);
        }
      }
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.error
        || (status === 404 ? 'Restart the backend so Note AI can load, then try again.' : null)
        || err?.message
        || 'Failed to run AI on this note';
      setError(message);
    } finally {
      window.setTimeout(() => setAiBusy(false), 280);
    }
  }, [aiBusy, aiPrompt, applyAiHtml, preferredLanguage, scanNoteActions]);

  const openCallReminderPicker = useCallback(async () => {
    const html = quillRef.current?.getEditor?.()?.root?.innerHTML || contentHtmlRef.current;
    const found = extractNeedToCallNamesFromNoteHtml(html);
    scanNoteActions(html);
    if (!found.length) {
      setError('No people found to add to call reminder');
      return;
    }
    setCallReminderOpen(true);
  }, [scanNoteActions]);

  const openNoteTasks = useCallback(() => {
    const html = quillRef.current?.getEditor?.()?.root?.innerHTML || contentHtmlRef.current;
    scanNoteActions(html);
    const buying = extractBuyingItemsFromNoteHtml(html);
    const items = extractTaskItemsFromNoteHtml(html, buying);
    if (!items.length) {
      setError('No tasks found in this note');
      return;
    }
    pendingTaskTitlesRef.current = items.slice(1);
    setTaskSeedTitle(items[0]);
    setTaskComposerOpen(true);
  }, [scanNoteActions]);

  const confirmCallReminderPeople = useCallback(async (selected) => {
    const names = (selected || []).map((name) => String(name || '').trim()).filter(Boolean);
    if (!names.length) {
      setCallReminderOpen(false);
      return;
    }
    setCallReminderSaving(true);
    setError('');
    try {
      let roster = users || [];
      if (!roster.length) {
        try {
          const result = await dispatch(fetchUsers());
          if (Array.isArray(result?.payload)) roster = result.payload;
        } catch {
          roster = users || [];
        }
      }
      let items = null;
      for (const name of names) {
        const matched = matchPersonBySpokenName(name, roster);
        const display = personLabel(matched, name);
        if (!display) continue;
        items = await appendNeedToCallItem({
          targetUserId: matched?.id || null,
          name: display,
          phone: String(matched?.mobileNumber || matched?.contactNumber || '').trim(),
          reason: 'From note',
        });
      }
      if (items) notifyNeedToCallChanged(items);
      setCallReminderOpen(false);
      setSelectedId(null);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not add to call reminder');
    } finally {
      setCallReminderSaving(false);
    }
  }, [dispatch, users]);

  const setNoteVersion = useCallback((original) => {
    const editor = quillRef.current?.getEditor?.();
    const current = editor?.root?.innerHTML || contentHtmlRef.current;
    if (original) {
      if (!viewingOriginalRef.current) {
        contentHtmlRef.current = current;
      }
      viewingOriginalRef.current = true;
      setViewingOriginal(true);
      const html = originalContentHtmlRef.current || '';
      const applied = setQuillHtml(editor, html) || html;
      setContentHtml(applied);
      return;
    }
    viewingOriginalRef.current = false;
    setViewingOriginal(false);
    const html = contentHtmlRef.current || '';
    const applied = setQuillHtml(editor, html) || html;
    setContentHtml(applied);
  }, []);

  const addSensitiveBlock = ({ title: blockTitle, entries, id, askPinOnCopy, attachments }) => {
    const files = normalizeSensitiveAttachments(attachments);
    if (credentialsView && !selectedIdRef.current) {
      const persistVault = async () => {
        try {
          const editing = vaultEditItemRef.current;
          vaultEditItemRef.current = null;
          if (editing?.noteId) {
            const full = await ensureHydratedNote(editing.noteId);
            const nextBlocks = normalizeSensitiveBlocks(full.sensitiveBlocks).map((block) => (
              block.id === editing.block.id
                ? { ...block, title: blockTitle, entries, askPinOnCopy: Boolean(askPinOnCopy), attachments: files }
                : block
            ));
            const updated = await updateNote(editing.noteId, { sensitiveBlocks: nextBlocks });
            setHydratedNotes((prev) => ({ ...prev, [editing.noteId]: updated }));
            setNotes((prev) => prev.map((item) => (
              item.id === editing.noteId ? redactNoteForList(updated) : item
            )));
            setExpandedVaultKey(editing.vaultKey);
            return;
          }
          const current = credentialBlocksRef.current;
          const next = id
            ? current.map((block) => (
              block.id === id
                ? { ...block, title: blockTitle, entries, askPinOnCopy: Boolean(askPinOnCopy), attachments: files }
                : block
            ))
            : [
              ...current,
              {
                id: newSensitiveId('secret'),
                title: blockTitle,
                entries,
                askPinOnCopy: Boolean(askPinOnCopy),
                attachments: files,
              },
            ];
          await persistCredentialBlocks(next);
          setExpandedVaultKey(id ? `bucket:${id}` : `bucket:${next[next.length - 1]?.id}`);
        } catch (err) {
          setError(err?.response?.data?.error || err?.message || 'Failed to save credential');
        }
      };
      persistVault();
      return;
    }
    if (id) {
      setSensitiveBlocks((prev) => {
        const next = prev.map((block) => (
          block.id === id
            ? { ...block, title: blockTitle, entries, askPinOnCopy: Boolean(askPinOnCopy), attachments: files }
            : block
        ));
        scheduleSave({ sensitiveBlocks: next });
        return next;
      });
      setExpandedSensitiveId(id);
      return;
    }
    const next = [
      ...sensitiveBlocks,
      {
        id: newSensitiveId('secret'),
        title: blockTitle,
        entries,
        askPinOnCopy: Boolean(askPinOnCopy),
        attachments: files,
      },
    ];
    setSensitiveBlocks(next);
    setExpandedSensitiveId(next[next.length - 1].id);
    scheduleSave({ sensitiveBlocks: next });
  };

  const withCredentialPin = (action) => {
    if (!pinEnabled) {
      action();
      return;
    }
    pendingPinActionRef.current = action;
    setPinModal('verify');
  };

  const openAddCredentials = (selectedText = '') => {
    vaultEditItemRef.current = null;
    setEditingSensitiveBlock(null);
    setSecretSeedText(selectedText || '');
    setSecretModalOpen(true);
  };

  const openSensitiveEditor = (itemOrBlock) => {
    const item = itemOrBlock?.block
      ? itemOrBlock
      : { source: 'note', noteId: null, vaultKey: itemOrBlock?.id, block: itemOrBlock };
    withCredentialPin(async () => {
      vaultEditItemRef.current = item.noteId ? item : (credentialsView ? { source: 'bucket', block: item.block } : null);
      let block = item.block;
      if (item.noteId) {
        const full = await ensureHydratedNote(item.noteId);
        block = normalizeSensitiveBlocks(full.sensitiveBlocks).find((entry) => entry.id === item.block.id) || item.block;
      }
      setSecretSeedText('');
      setEditingSensitiveBlock(block);
      setSecretModalOpen(true);
    });
  };

  const toggleVaultCredential = (item) => {
    const nextKey = expandedVaultKey === item.vaultKey ? null : item.vaultKey;
    if (!nextKey) {
      setExpandedVaultKey(null);
      return;
    }
    setExpandedVaultKey(nextKey);
    if (item.noteId) {
      ensureHydratedNote(item.noteId).catch(() => {});
    }
  };

  const removeVaultCredential = (item) => {
    withCredentialPin(async () => {
      try {
        if (item.noteId) {
          const full = await ensureHydratedNote(item.noteId);
          const nextBlocks = normalizeSensitiveBlocks(full.sensitiveBlocks).filter((block) => block.id !== item.block.id);
          const updated = await updateNote(item.noteId, { sensitiveBlocks: nextBlocks });
          setHydratedNotes((prev) => ({ ...prev, [item.noteId]: updated }));
          setNotes((prev) => prev.map((note) => (
            note.id === item.noteId ? redactNoteForList(updated) : note
          )));
        } else {
          const next = credentialBlocksRef.current.filter((block) => block.id !== item.block.id);
          await persistCredentialBlocks(next);
        }
        setExpandedVaultKey((current) => (current === item.vaultKey ? null : current));
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Failed to remove credential');
      }
    });
  };

  const toggleSensitiveBlock = (blockId) => {
    setExpandedSensitiveId((current) => {
      const next = current === blockId ? null : blockId;
      if (next) {
        window.requestAnimationFrame(() => {
          document.getElementById(`sensitive-block-${blockId}`)?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        });
      }
      return next;
    });
  };

  const copySensitiveValue = async (entry, requirePin, source = {}) => {
    const markCopied = () => {
      setCopiedSecretId(entry.id);
      window.setTimeout(() => {
        setCopiedSecretId((current) => (current === entry.id ? null : current));
      }, 1600);
    };
    const resolveSecret = async () => {
      let secret = entry?.value;
      if (!secret && source.noteId) {
        const full = await ensureHydratedNote(source.noteId);
        const block = normalizeSensitiveBlocks(full.sensitiveBlocks)
          .find((item) => item.id === source.blockId);
        const match = (block?.entries || []).find((item) => item.id === entry.id || item.key === entry.key);
        secret = match?.value;
      }
      if (!secret && source.blockId) {
        const block = credentialBlocksRef.current.find((item) => item.id === source.blockId);
        const match = (block?.entries || []).find((item) => item.id === entry.id || item.key === entry.key);
        secret = match?.value;
      }
      return secret;
    };
    const doCopy = async (secret) => {
      try {
        await copyTextToClipboard(secret);
        markCopied();
      } catch {
        setError('Could not copy to clipboard');
      }
    };
    const afterUnlock = async () => {
      const secret = await resolveSecret();
      if (!secret) {
        setError('Could not open this value. Try again after entering your PIN.');
        return;
      }
      if (entry.valueVisible) {
        await doCopy(secret);
        return;
      }
      setRevealSecret({
        title: entry.key,
        secret,
        entryId: entry.id,
      });
    };
    if (requirePin && pinEnabled) {
      withCredentialPin(afterUnlock);
      return;
    }
    await afterUnlock();
  };

  const removeSensitiveBlock = (blockId) => {
    withCredentialPin(() => {
      setSensitiveBlocks((prev) => {
        const next = prev.filter((item) => item.id !== blockId);
        scheduleSave({ sensitiveBlocks: next });
        return next;
      });
    });
  };

  const insertImageInEditor = useCallback((uploaded) => {
    const editor = quillRef.current?.getEditor?.();
    const fileKey = uploaded?.fileUrl;
    if (!editor || !fileKey) return;
    const imageUrl = buildLegacyFileUrl(fileKey) || fileKey;
    const range = editor.getSelection(true);
    const index = range ? range.index : Math.max(0, editor.getLength() - 1);
    editor.insertEmbed(index, 'image', imageUrl, 'user');
    try {
      const [leaf] = editor.getLeaf(index) || [];
      const node = leaf?.domNode;
      if (node && String(node.tagName || '').toUpperCase() === 'IMG') {
        node.setAttribute('data-file-key', extractInternalFileKey(fileKey) || fileKey);
      }
    } catch {
      // ignore
    }
    editor.setSelection(index + 1, 0);
    const html = editor.root.innerHTML;
    contentHtmlRef.current = html;
    setContentHtml(html);
    scheduleSave({ contentHtml: html });
  }, [scheduleSave]);

  const keepUploadedFile = useCallback((uploaded) => {
    if (!uploaded?.fileUrl) return;
    setAttachments((prev) => {
      if (prev.some((item) => item.fileUrl === uploaded.fileUrl)) return prev;
      const next = [...prev, {
        id: uploaded.id || `att-${Date.now()}`,
        fileName: uploaded.fileName || 'file',
        fileUrl: uploaded.fileUrl,
        fileType: uploaded.fileType || inferAttachmentFileType({ fileName: uploaded.fileName }),
        fileSize: uploaded.fileSize || 0,
      }];
      scheduleSave({ attachments: next });
      return next;
    });
  }, [scheduleSave]);

  const addAttachments = useCallback(async (files, { insertImages = true } = {}) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length || !selectedIdRef.current) return;
    setUploading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const results = await uploadFilesWithProgress({
        apiUrl: API_URL,
        token,
        files: list,
      });
      const uploaded = results.map((item, index) => {
        const data = item?.data || {};
        const file = list[index];
        return {
          id: data.id || `${Date.now()}-${index}`,
          fileName: data.fileName || file?.name || 'file',
          fileUrl: data.fileUrl || data.fileKey || data.url,
          fileType: data.fileType || inferAttachmentFileType({ fileName: file?.name }),
          fileSize: data.fileSize || file?.size || 0,
        };
      }).filter((item) => item.fileUrl);
      if (!uploaded.length) return;
      uploaded.forEach((item) => {
        keepUploadedFile(item);
        if (insertImages && String(item.fileType || '').toUpperCase() === 'IMAGE') {
          insertImageInEditor(item);
        }
      });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to attach file');
    } finally {
      setUploading(false);
    }
  }, [insertImageInEditor, keepUploadedFile, scheduleSave]);

  const handlePasteFiles = useCallback((files) => {
    addAttachments(files, { insertImages: false });
  }, [addAttachments]);

  const fileAttachments = attachments.filter((item) => !isLinkAttachment(item));

  const openCredentialAttachment = (block, attachment, source = {}) => {
    const run = async () => {
      let files = normalizeSensitiveAttachments(block?.attachments).filter((item) => item.fileUrl);
      if (!files.length && selectedIdRef.current && !source.noteId && !source.blockId) {
        const full = await ensureHydratedNote(selectedIdRef.current);
        const hydrated = normalizeSensitiveBlocks(full.sensitiveBlocks)
          .find((item) => item.id === block.id);
        files = normalizeSensitiveAttachments(hydrated?.attachments).filter((item) => item.fileUrl);
      }
      if (source.noteId) {
        const full = await ensureHydratedNote(source.noteId);
        const hydrated = normalizeSensitiveBlocks(full.sensitiveBlocks)
          .find((item) => item.id === (source.blockId || block.id));
        files = normalizeSensitiveAttachments(hydrated?.attachments).filter((item) => item.fileUrl);
      } else if (source.blockId) {
        const bucket = credentialBlocksRef.current.find((item) => item.id === source.blockId);
        files = normalizeSensitiveAttachments(bucket?.attachments).filter((item) => item.fileUrl);
      }
      const current = files.find((item) => (
        item.id === attachment.id || (attachment.fileUrl && item.fileUrl === attachment.fileUrl)
      )) || attachment;
      if (!current?.fileUrl) {
        setError('Could not open this file. Try again after entering your PIN.');
        return;
      }
      openAttachmentViewer(current, files);
    };
    if (pinEnabled) {
      withCredentialPin(run);
      return;
    }
    run();
  };

  const openAttachmentViewer = (item, overrideItems) => {
    const files = Array.isArray(overrideItems)
      ? overrideItems.filter((entry) => !isLinkAttachment(entry))
      : attachments.filter((entry) => !isLinkAttachment(entry));
    const idx = files.findIndex((entry) => (
      entry.id === item.id || (item.fileUrl && entry.fileUrl === item.fileUrl)
    ));
    if (idx >= 0) {
      setViewerItems(files);
      setViewerIndex(idx);
      return;
    }
    if (item?.fileUrl) {
      setViewerItems([item, ...files]);
      setViewerIndex(0);
    }
  };

  const handleAttachClick = () => {
    attachInputRef.current?.click();
  };

  const handleAddLink = async () => {
    let seedUrl = '';
    try {
      if (navigator.clipboard?.readText) {
        seedUrl = urlFromClipboardText(await navigator.clipboard.readText());
      }
    } catch {
      seedUrl = '';
    }
    setLinkSeedUrl(seedUrl);
    setLinkModalOpen(true);
  };

  const saveNoteLink = ({ url, fileName, previewImageUrl, previewDescription, siteName, domain }) => {
    const next = {
      id: `link-${Date.now()}`,
      fileName,
      fileUrl: url,
      fileType: 'LINK',
      fileSize: 0,
      linkUrl: url,
      previewImageUrl,
      previewDescription,
      siteName,
      domain,
    };
    setAttachments((prev) => {
      const list = [...prev, next];
      scheduleSave({ attachments: list });
      return list;
    });
  };

  const renameAttachment = (attachmentId, fileName) => {
    const nextName = String(fileName || '').trim();
    if (!nextName) {
      setEditingAttachmentId(null);
      return;
    }
    setAttachments((prev) => {
      const list = prev.map((item) => (
        item.id === attachmentId ? { ...item, fileName: nextName } : item
      ));
      scheduleSave({ attachments: list });
      return list;
    });
    setEditingAttachmentId(null);
  };

  const removeAttachment = (attachmentId) => {
    const target = attachments.find((item) => item.id === attachmentId)
      || attachments.find((item) => item.fileUrl === attachmentId);
    const next = attachments.filter((item) => item !== target && item.id !== attachmentId);
    let html = contentHtmlRef.current;
    if (target?.fileUrl) {
      const editor = quillRef.current?.getEditor?.();
      if (editor?.root) {
        Array.from(editor.root.querySelectorAll('img')).forEach((img) => {
          if (htmlHasAttachment(img.getAttribute('src') || '', target)) {
            img.remove();
          }
        });
        html = editor.root.innerHTML;
        contentHtmlRef.current = html;
        setContentHtml(html);
      }
    }
    setAttachments(next);
    scheduleSave({ attachments: next, contentHtml: html });
  };

  useEffect(() => {
    if (!selectedId) return undefined;
    const timer = window.setTimeout(() => {
      const editor = quillRef.current?.getEditor?.();
      if (!editor) return;
      editor.focus();
    }, 40);
    return () => window.clearTimeout(timer);
  }, [selectedId]);

  useEffect(() => {
    const root = quillRef.current?.getEditor?.()?.root;
    if (!root || !selectedId) return undefined;
    const onDblClick = (event) => {
      const img = event.target?.closest?.('img');
      if (!img || !root.contains(img)) return;
      event.preventDefault();
      const fileKey = fileKeyFromSrc(img.getAttribute('src'));
      const src = img.getAttribute('src');
      const match = fileAttachments.find((entry) => (
        entry.fileUrl === fileKey || entry.fileUrl === src
      ));
      if (match) {
        openAttachmentViewer(match);
        return;
      }
      openAttachmentViewer({
        id: `inline-${Date.now()}`,
        fileUrl: fileKey || src,
        fileName: img.getAttribute('alt') || 'Image',
        fileType: 'IMAGE',
      });
    };
    root.addEventListener('dblclick', onDblClick);
    return () => root.removeEventListener('dblclick', onDblClick);
  }, [selectedId, attachments]);

  const saveLabel = saveState === 'saving' || uploading
    ? 'Saving…'
    : saveState === 'unsaved'
      ? 'Saving…'
      : saveState === 'error'
        ? 'Save failed'
        : 'Saved';

  const listNotes = useMemo(() => notes.filter((note) => {
    const title = String(note?.title || '').trim().toLowerCase();
    if (title === 'credentials' && !stripHtml(note.contentHtml)) return false;
    const caption = isAudioCaptionNoteTitle(note?.title);
    return captionsView ? caption : !caption;
  }), [notes, captionsView]);

  const filteredNotes = useMemo(() => {
    const query = listSearch.trim().toLowerCase();
    return listNotes.filter((note) => {
      if (!captionsView && listTagFilter && !noteHasTag(note, listTagFilter)) return false;
      if (query && !noteSearchText(note).includes(query)) return false;
      return true;
    });
  }, [captionsView, listNotes, listSearch, listTagFilter]);

  const usedTags = useMemo(() => {
    const seen = new Set();
    const out = [];
    notes.forEach((note) => {
      if (isAudioCaptionNoteTitle(note?.title)) return;
      normalizeNoteTags(note?.tags).forEach((tag) => {
        const key = tag.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        out.push(tag);
      });
    });
    return out;
  }, [notes]);

  const allCredentialItems = useMemo(() => {
    const items = credentialBlocks.map((block) => ({
      source: 'bucket',
      noteId: null,
      noteTitle: '',
      vaultKey: `bucket:${block.id}`,
      block,
    }));
    notes.forEach((note) => {
      const title = String(note?.title || '').trim().toLowerCase();
      if (title === 'credentials' && !stripHtml(note.contentHtml)) return;
      const source = hydratedNotes[note.id] || note;
      normalizeSensitiveBlocks(source.sensitiveBlocks).forEach((block) => {
        items.push({
          source: 'note',
          noteId: note.id,
          noteTitle: noteTitle(note),
          vaultKey: `note:${note.id}:${block.id}`,
          block,
        });
      });
    });
    return items;
  }, [credentialBlocks, notes, hydratedNotes]);

  const filteredCredentials = useMemo(() => {
    const query = credentialSearch.trim().toLowerCase();
    if (!query) return allCredentialItems;
    return allCredentialItems.filter((item) => {
      const hay = [
        item.noteTitle,
        item.block.title,
        ...((item.block.entries || []).map((entry) => entry.key)),
        ...((item.block.attachments || []).map((file) => file.fileName)),
      ].join(' ').toLowerCase();
      return hay.includes(query);
    });
  }, [allCredentialItems, credentialSearch]);

  const jumpToNoteMatch = useCallback((direction, queryOverride) => {
    const query = String(queryOverride ?? noteSearch).trim();
    const editor = quillRef.current?.getEditor?.();
    if (!query || !editor) {
      setNoteMatchIndex(0);
      return;
    }
    const text = String(editor.getText() || '');
    const lower = text.toLowerCase();
    const needle = query.toLowerCase();
    const total = countQueryMatches(lower, needle);
    if (!total) {
      setNoteMatchIndex(0);
      return;
    }
    const selection = editor.getSelection();
    const from = direction === 'first'
      ? 0
      : selection
        ? selection.index + (direction === 'prev' ? -1 : Math.max(selection.length, 1))
        : 0;
    let found = direction === 'prev'
      ? lower.lastIndexOf(needle, Math.max(from, 0))
      : lower.indexOf(needle, from);
    if (found < 0) {
      found = direction === 'prev' ? lower.lastIndexOf(needle) : lower.indexOf(needle);
    }
    if (found < 0) {
      setNoteMatchIndex(0);
      return;
    }
    editor.setSelection(found, needle.length, 'silent');
    editor.scrollSelectionIntoView?.();
    const root = editor.root;
    const sel = window.getSelection?.();
    if (sel?.rangeCount && root) {
      sel.getRangeAt(0).startContainer?.parentElement?.scrollIntoView?.({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
    let ordinal = 0;
    let cursor = 0;
    while (cursor <= found) {
      const at = lower.indexOf(needle, cursor);
      if (at < 0 || at > found) break;
      ordinal += 1;
      cursor = at + Math.max(needle.length, 1);
    }
    setNoteMatchIndex(ordinal);
  }, [noteSearch]);

  useEffect(() => {
    let frame = 0;
    const run = () => {
      const editor = quillRef.current?.getEditor?.();
      const root = editor?.root;
      const query = noteSearch.trim();
      if (!selectedId || !query || !root) {
        clearNoteSearchHighlights();
        return;
      }
      applyNoteSearchHighlights(root, query, noteMatchIndex);
    };
    frame = window.requestAnimationFrame(run);
    return () => {
      window.cancelAnimationFrame(frame);
      clearNoteSearchHighlights();
    };
  }, [noteMatchIndex, noteSearch, selectedId]);

  const headerSubtitle = selectedId
    ? (title.trim() || 'Untitled note')
    : credentialsView
      ? `${allCredentialItems.length} credential${allCredentialItems.length === 1 ? '' : 's'}`
      : captionsView
        ? `${listNotes.length} caption note${listNotes.length === 1 ? '' : 's'}`
        : `${listNotes.length} note${listNotes.length === 1 ? '' : 's'} · autosave`;

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-50 dark:bg-gray-950">
      <style>{`
        ::highlight(note-search-hit) {
          background-color: #fde68a;
          color: inherit;
        }
        ::highlight(note-search-current) {
          background-color: #f59e0b;
          color: #111827;
        }
      `}</style>
      <div className="flex-shrink-0 border-b border-gray-200/80 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {selectedId || credentialsView ? (
              <button
                type="button"
                onClick={handleBackToList}
                className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                aria-label="Back to all notes"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            ) : (
              <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M7 4h7l5 5v11a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z" />
                </svg>
              </span>
            )}
            <div className="min-w-0">
              <h1 className="text-[15px] font-semibold text-gray-900 dark:text-white leading-tight">
                {credentialsView ? 'Credentials' : captionsView ? 'Caption notes' : 'Notes'}
              </h1>
              <p className="truncate text-[11px] text-gray-500 dark:text-gray-400">{headerSubtitle}</p>
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center gap-1.5">
            {!selectedId && !credentialsView && !captionsView ? (
              <button
                type="button"
                onClick={() => setPinModal(pinEnabled ? 'change' : 'setup')}
                className="px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                {pinEnabled ? 'PIN' : 'Set PIN'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={openCredentialsView}
              className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg border ${
                credentialsView
                  ? 'border-amber-400 bg-amber-50 text-amber-900 dark:border-amber-500 dark:bg-amber-500/20 dark:text-amber-50'
                  : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              Credentials
            </button>
            {!captionsView ? (
            <button
              type="button"
              onClick={handleNewNote}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 shadow-sm"
            >
              New note
            </button>
            ) : null}
            {onClose ? (
              <button
                type="button"
                onClick={async () => {
                  await flushSave();
                  onClose();
                }}
                className="ml-0.5 inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-gray-800 dark:hover:text-gray-100"
                aria-label="Close notes"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden">
      <div className={`min-w-0 min-h-0 flex-1 flex flex-col overflow-hidden ${!selectedId ? 'md:w-[76%] md:flex-none' : ''}`}>
      {error ? (
        <div className="mx-4 mt-3 rounded-xl border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 px-3 py-2 text-xs text-rose-700 dark:text-rose-200">
          {error}
        </div>
      ) : null}

      {!selectedId && credentialsView ? (
        <div className="flex-1 min-h-0 overflow-y-auto p-5">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <NotesSearchBox
              value={credentialSearch}
              onChange={setCredentialSearch}
              placeholder="Search credentials"
            />
            <button
              type="button"
              onClick={() => openAddCredentials()}
              className="flex-shrink-0 px-3 py-2 text-xs font-semibold rounded-xl bg-amber-500 text-white hover:bg-amber-600 shadow-sm"
            >
              Add credentials
            </button>
          </div>
          {credentialSearch.trim() ? (
            <p className="mb-3 text-[11px] text-gray-400">
              {filteredCredentials.length} credential{filteredCredentials.length === 1 ? '' : 's'} found
            </p>
          ) : null}
          {filteredCredentials.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-amber-300/80 dark:border-amber-800 bg-white dark:bg-gray-900 px-6 py-14 text-center">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {allCredentialItems.length === 0 ? 'No credentials yet' : `No credentials match “${credentialSearch.trim()}”.`}
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {allCredentialItems.length === 0
                  ? 'Save logins from chat or add one here. Note credentials show up in this list too.'
                  : 'Try a different title or field name.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {filteredCredentials.map((item) => {
                const expanded = expandedVaultKey === item.vaultKey;
                const entries = Array.isArray(item.block.entries) ? item.block.entries : [];
                const files = Array.isArray(item.block.attachments) ? item.block.attachments : [];
                const manyCredentials = filteredCredentials.length > 1;
                return (
                  <div
                    key={item.vaultKey}
                    className={`min-w-0 self-start overflow-hidden rounded-2xl border border-amber-200/80 bg-white shadow-sm dark:border-amber-800/50 dark:bg-gray-900 ${expanded && manyCredentials ? 'sm:col-span-3' : ''}`}
                  >
                    <div className="flex items-center gap-1 px-3 py-2.5">
                      <button
                        type="button"
                        onClick={() => toggleVaultCredential(item)}
                        className="min-w-0 flex-1 flex items-center gap-2 text-left"
                        aria-expanded={expanded}
                      >
                        <span className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300" aria-hidden="true">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                          </svg>
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-gray-900 dark:text-white truncate">
                            {credentialSearch.trim()
                              ? highlightPlainText(item.block.title, credentialSearch)
                              : item.block.title}
                          </span>
                          {item.noteTitle ? (
                            <span className="block text-[11px] text-gray-400 truncate">
                              From note · {item.noteTitle}
                            </span>
                          ) : (
                            <span className="block text-[11px] text-gray-400">Credential vault</span>
                          )}
                        </span>
                        <svg
                          className={`w-4 h-4 flex-shrink-0 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => openSensitiveEditor(item)}
                        className="flex-shrink-0 p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        aria-label={`Update ${item.block.title}`}
                      >
                        <EditPencilIcon />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeVaultCredential(item)}
                        className="text-gray-400 hover:text-rose-500 px-0.5 flex-shrink-0"
                        aria-label={`Remove ${item.block.title}`}
                      >
                        ×
                      </button>
                    </div>
                    {expanded ? (
                      <div className="border-t border-gray-100 dark:border-gray-800 px-3 py-3">
                        <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
                          {entries.map((entry) => {
                            const revealed = Boolean(entry && entry.valueVisible);
                            return (
                              <div
                                key={entry.id}
                                className="rounded-xl bg-slate-50 dark:bg-gray-800/70 px-2.5 py-2 min-w-0"
                              >
                                <p className="text-[11px] font-medium text-gray-700 dark:text-gray-200 truncate">
                                  {credentialSearch.trim()
                                    ? highlightPlainText(entry.key, credentialSearch)
                                    : entry.key}
                                </p>
                                <div className="mt-1 flex items-center gap-1">
                                  <span className={`min-w-0 flex-1 text-[11px] truncate ${
                                    revealed ? 'text-gray-800 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'
                                  }`}>
                                    {revealed ? (entry.value || '') : '••••••••'}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => copySensitiveValue(entry, !entry.valueVisible, {
                                      noteId: item.noteId,
                                      blockId: item.block.id,
                                    })}
                                    className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-100"
                                  >
                                    {copiedSecretId === entry.id ? 'Copied' : (revealed ? 'Copy' : 'Open')}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {files.length ? (
                          <ul className="mt-3 space-y-1.5">
                            {files.map((file) => (
                              <li key={file.id || file.fileName}>
                                <button
                                  type="button"
                                  onClick={() => openCredentialAttachment(item.block, file, {
                                    noteId: item.noteId,
                                    blockId: item.block.id,
                                  })}
                                  className="w-full flex items-center gap-2 rounded-xl bg-slate-50 dark:bg-gray-800/70 px-2.5 py-2 text-left hover:bg-slate-100 dark:hover:bg-gray-800"
                                >
                                  <FileTypeIcon fileName={file.fileName} fileType={file.fileType} size="sm" />
                                  <span className="min-w-0 flex-1">
                                    <span className="block text-[11px] font-medium text-gray-800 dark:text-gray-100 truncate">
                                      {file.fileName || 'File'}
                                    </span>
                                    <span className="block text-[10px] text-gray-400">
                                      {pinEnabled ? 'Locked · tap to open with PIN' : 'Tap to open'}
                                    </span>
                                  </span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => openSensitiveEditor(item)}
                          className="mt-2 text-[11px] font-semibold text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          Update
                        </button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : !selectedId ? (
        <div className="flex-1 min-h-0 overflow-y-auto p-5">
          <div className="mb-4 flex rounded-2xl bg-gray-100 p-1 dark:bg-gray-800">
            <button
              type="button"
              onClick={() => { setCaptionsView(false); }}
              className={`flex-1 rounded-xl px-3 py-2 text-sm font-semibold ${
                !captionsView
                  ? 'bg-white text-primary-700 shadow-sm dark:bg-gray-900 dark:text-primary-300'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              Notes
            </button>
            <button
              type="button"
              onClick={() => { setCaptionsView(true); setListTagFilter(''); }}
              className={`flex-1 rounded-xl px-3 py-2 text-sm font-semibold ${
                captionsView
                  ? 'bg-white text-primary-700 shadow-sm dark:bg-gray-900 dark:text-primary-300'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              Caption notes
            </button>
          </div>
          {listNotes.length > 0 ? (
            <div className="mb-4">
              <NotesSearchBox
                value={listSearch}
                onChange={setListSearch}
                placeholder={captionsView ? 'Search caption notes' : 'Search notes'}
              />
              {listSearch.trim() || listTagFilter ? (
                <p className="mt-2 text-[11px] text-gray-400">
                  {filteredNotes.length} note{filteredNotes.length === 1 ? '' : 's'} found
                </p>
              ) : null}
              {listTagFilter ? (
                <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-gray-200/80 bg-white/60 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/60">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Filter
                  </span>
                  <span
                    className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
                    style={{
                      backgroundImage: tagColor(listTagFilter).gradient,
                      color: tagColor(listTagFilter).text,
                    }}
                  >
                    <TagIcon tag={listTagFilter} />
                    {listTagFilter}
                    <button
                      type="button"
                      onClick={() => setListTagFilter('')}
                      className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-current/70 hover:bg-white/40"
                      aria-label="Clear tag filter"
                    >
                      ×
                    </button>
                  </span>
                  <button
                    type="button"
                    onClick={() => setListTagFilter('')}
                    className="text-[11px] font-semibold text-primary-600 hover:underline dark:text-primary-400"
                  >
                    Clear
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
          {loading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading notes…</p>
          ) : listNotes.length === 0 ? (
            captionsView ? (
              <div className="w-full rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-20 text-center">
                <p className="text-base font-semibold text-gray-900 dark:text-white">No caption notes yet</p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Live captions from the mic are saved here.
                </p>
              </div>
            ) : (
            <button
              type="button"
              onClick={handleNewNote}
              className="w-full rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-20 text-center hover:border-primary-400 dark:hover:border-primary-500 hover:shadow-sm transition-colors"
            >
              <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-200">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </span>
              <p className="text-base font-semibold text-gray-900 dark:text-white">Create your first note</p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Type, paste an image, or drop a file. Everything autosaves.
              </p>
            </button>
            )
          ) : filteredNotes.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
              {listTagFilter && !captionsView
                ? `No notes tagged “${listTagFilter}”.`
                : captionsView
                  ? `No caption notes match “${listSearch.trim()}”.`
                  : `No notes match “${listSearch.trim()}”.`}
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
              {filteredNotes.map((note) => {
                const snippet = stripHtml(note.contentHtml);
                const savedTitle = String(note?.title || '').trim();
                const displayTitle = noteTitle(note);
                const showSnippet = Boolean(snippet)
                  && snippet !== savedTitle
                  && snippet !== displayTitle;
                const emptyBody = !snippet;
                const thumb = firstImageSrc(note.contentHtml)
                  || (note.attachments || []).find((item) => isDisplayableImage(item))?.fileUrl;
                return (
                  <div
                    key={note.id}
                    className="group relative w-full min-w-0 flex flex-col text-left overflow-visible rounded-2xl border border-gray-200/90 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md transition-all"
                  >
                    <button
                      type="button"
                      onClick={(event) => openShareNote(note, event)}
                      className="absolute top-2 right-10 z-10 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/95 text-gray-500 shadow-sm ring-1 ring-gray-200 hover:bg-sky-50 hover:text-sky-700 dark:bg-gray-900/95 dark:text-gray-300 dark:ring-gray-600 dark:hover:bg-sky-950/60 dark:hover:text-sky-300"
                      aria-label={`Share ${displayTitle}`}
                      title="Share note"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7M16 6l-4-4-4 4M12 2v13" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={(event) => requestDeleteNote(note, event)}
                      className="absolute top-2 right-2 z-10 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/95 text-gray-500 shadow-sm ring-1 ring-gray-200 hover:bg-rose-50 hover:text-rose-600 dark:bg-gray-900/95 dark:text-gray-300 dark:ring-gray-600 dark:hover:bg-rose-950/60 dark:hover:text-rose-300"
                      aria-label={`Delete ${displayTitle}`}
                      title="Delete note"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => openNote(note)}
                      className="w-full min-w-0 flex flex-col text-left"
                    >
                    {thumb ? (
                      <div className="w-full h-28 bg-gray-100 dark:bg-gray-800 overflow-hidden rounded-t-2xl">
                        {String(thumb).startsWith('http') || String(thumb).includes('/files/') ? (
                          <PresignedImage
                            fileKey={fileKeyFromSrc(thumb) || thumb}
                            alt=""
                            className="w-full h-28 object-cover"
                          />
                        ) : (
                          <PresignedImage
                            fileKey={thumb}
                            alt=""
                            className="w-full h-28 object-cover"
                          />
                        )}
                      </div>
                    ) : null}
                    <div className="w-full px-3.5 pt-3 pb-1">
                      <h3 className="w-full text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {listSearch.trim() ? highlightPlainText(displayTitle, listSearch) : displayTitle}
                      </h3>
                    </div>
                    <div className="w-full px-3.5 pb-3 pt-1 flex-1">
                      {showSnippet ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-3 min-h-[2.5rem] leading-5">
                          {listSearch.trim() ? highlightPlainText(snippet, listSearch) : snippet}
                        </p>
                      ) : emptyBody ? (
                        <p className="text-xs text-gray-400 dark:text-gray-500">Empty note</p>
                      ) : null}
                      <p className={`text-[10px] text-gray-400 ${showSnippet || emptyBody ? 'mt-2.5' : 'mt-1'}`}>
                        {formatUpdated(note.updatedAt || note.createdAt)}
                        {(note.attachments || []).length
                          ? ` · ${note.attachments.length} file${note.attachments.length === 1 ? '' : 's'}`
                          : ''}
                        {(note.sensitiveBlocks || []).length
                          ? ` · ${note.sensitiveBlocks.length} credential${note.sensitiveBlocks.length === 1 ? '' : 's'}`
                          : ''}
                      </p>
                      {String(note.shareKind || '').toUpperCase() === 'LIVE' ? (
                        <p className="mt-1 text-[10px] font-semibold text-sky-600">Live sync{note.sharedByName ? ` · ${note.sharedByName}` : ''}</p>
                      ) : String(note.shareKind || '').toUpperCase() === 'COPY' ? (
                        <p className="mt-1 text-[10px] font-semibold text-violet-600">Shared copy{note.sharedByName ? ` · ${note.sharedByName}` : ''}</p>
                      ) : null}
                    </div>
                    </button>
                    <div
                      className="px-3.5 pb-3"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <NoteTagChips
                        tags={note.tags}
                        activeTag={listTagFilter}
                        adding={addingTagForId === note.id}
                        draft={addingTagForId === note.id ? tagDraft : ''}
                        onDraftChange={setTagDraft}
                        onStartAdd={() => {
                          setAddingTagForId(note.id);
                          setTagDraft('');
                        }}
                        onCommitAdd={() => commitTagDraft(note.id, note.tags)}
                        onCancelAdd={() => {
                          setAddingTagForId(null);
                          setTagDraft('');
                        }}
                        onSelectTag={toggleTagFilter}
                        onRemoveTag={(tag) => removeNoteTag(note.id, note.tags, tag)}
                        onPickSuggestion={(tag) => pickSuggestedTag(note.id, note.tags, tag)}
                        usedTags={usedTags}
                        addPosition="bottomRight"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-200/80 dark:border-gray-800 bg-white dark:bg-gray-900">
            <input
              value={title}
              onChange={(event) => handleTitleChange(event.target.value)}
              onFocus={checkLiveNoteTimestamp}
              onBlur={() => {
                flushSave();
              }}
              placeholder="Note title"
              className="min-w-0 flex-1 rounded-xl bg-slate-50 dark:bg-gray-800 px-3 py-2 text-lg font-semibold text-gray-900 dark:text-white placeholder-gray-400 border border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-300"
            />
            <span className={`flex-shrink-0 text-[11px] font-medium ${saveState === 'error' ? 'text-rose-600' : 'text-gray-400'}`}>
              {saveLabel}
            </span>
            <button
              type="button"
              onClick={() => openTaskComposer()}
              className="flex-shrink-0 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700"
            >
              Create task
            </button>
            {shareMeta?.shareKind === 'LIVE' ? (
              <button
                type="button"
                onClick={handleLiveRefresh}
                disabled={refreshingLive}
                className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 disabled:opacity-60"
                title="Refresh live note"
              >
                <svg className={`h-3.5 w-3.5 ${refreshingLive ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v6h6M20 20v-6h-6M5 19A9 9 0 0019 8M19 5A9 9 0 005 16" />
                </svg>
                {refreshingLive ? 'Refreshing' : 'Refresh'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => openShareNote()}
              className="flex-shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-sky-50 hover:text-sky-700 dark:hover:bg-sky-950/40 dark:hover:text-sky-300"
              aria-label="Share note"
              title="Share note"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7M16 6l-4-4-4 4M12 2v13" />
              </svg>
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="flex-shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40 dark:hover:text-rose-300"
              aria-label="Delete note"
              title="Delete note"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {shareMeta?.shareKind === 'LIVE' && liveNoteStale ? (
            <div className="flex items-center justify-between gap-3 px-5 py-2 border-b border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/40">
              <p className="text-sm font-semibold text-red-600 dark:text-red-400">
                Note has been updated
              </p>
              <button
                type="button"
                onClick={handleLiveRefresh}
                disabled={refreshingLive}
                className="flex-shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              >
                {refreshingLive ? 'Loading' : 'Load'}
              </button>
            </div>
          ) : null}
          <div className="flex items-center gap-2 px-5 py-2 border-b border-gray-200/80 dark:border-gray-800 bg-white dark:bg-gray-900">
            <div className="min-w-0 flex-1">
              <NoteTagChips
                tags={tags}
                activeTag={listTagFilter}
                adding={addingTagForId === selectedId}
                draft={addingTagForId === selectedId ? tagDraft : ''}
                onDraftChange={setTagDraft}
                onStartAdd={() => {
                  setAddingTagForId(selectedId);
                  setTagDraft('');
                }}
                onCommitAdd={() => commitTagDraft(selectedId, tags)}
                onCancelAdd={() => {
                  setAddingTagForId(null);
                  setTagDraft('');
                }}
                onSelectTag={(tag) => {
                  toggleTagFilter(tag);
                  applyNote(null);
                }}
                onRemoveTag={(tag) => removeNoteTag(selectedId, tags, tag)}
                onPickSuggestion={(tag) => pickSuggestedTag(selectedId, tags, tag)}
                usedTags={usedTags}
              />
            </div>
            <div
              className={`flex-shrink-0 overflow-hidden transition-all duration-200 ${
                noteSearchOpen || noteSearch.trim() ? 'w-[55%]' : 'w-[30%]'
              }`}
            >
              <NotesSearchBox
                compact
                value={noteSearch}
                onChange={(value) => {
                  setNoteSearch(value);
                  setNoteMatchIndex(0);
                }}
                onFocus={() => setNoteSearchOpen(true)}
                onBlur={() => {
                  if (!noteSearch.trim()) setNoteSearchOpen(false);
                }}
                placeholder={noteSearchOpen || noteSearch.trim() ? 'Search in this note' : 'Search'}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;
                  event.preventDefault();
                  jumpToNoteMatch(event.shiftKey ? 'prev' : (noteMatchIndex ? 'next' : 'first'));
                }}
                suffix={noteSearch.trim() ? (
                  <>
                    <span className="text-[10px] text-gray-400 tabular-nums">
                      {countQueryMatches(stripHtml(contentHtmlRef.current), noteSearch)
                        ? `${noteMatchIndex || 1}/${countQueryMatches(stripHtml(contentHtmlRef.current), noteSearch)}`
                        : '0'}
                    </span>
                    <button
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => jumpToNoteMatch('prev')}
                      className="px-1 text-[11px] text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                      aria-label="Previous match"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => jumpToNoteMatch(noteMatchIndex ? 'next' : 'first')}
                      className="px-1 text-[11px] text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                      aria-label="Next match"
                    >
                      ↓
                    </button>
                  </>
                ) : null}
              />
            </div>
          </div>
          <div className="flex-1 min-h-0 flex flex-col md:flex-row">
            <div className="flex-1 md:w-[80%] md:flex-none min-w-0 min-h-0 flex flex-col overflow-hidden p-4">
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-2xl border border-gray-200/90 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
              {String(originalContentHtmlRef.current || '').trim() ? (
                <div className="flex gap-1 border-b border-gray-200 p-2 dark:border-gray-800">
                  <button
                    type="button"
                    onClick={() => setNoteVersion(false)}
                    className={`flex-1 rounded-lg px-2 py-1.5 text-[12px] font-semibold ${
                      viewingOriginal
                        ? 'text-gray-500 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800'
                        : 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-white'
                    }`}
                  >
                    Structured
                  </button>
                  <button
                    type="button"
                    onClick={() => setNoteVersion(true)}
                    className={`flex-1 rounded-lg px-2 py-1.5 text-[12px] font-semibold ${
                      viewingOriginal
                        ? 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-white'
                        : 'text-gray-500 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800'
                    }`}
                  >
                    Original
                  </button>
                </div>
              ) : null}
              <div className="flex-1 min-h-0">
              <CompactRichEditor
                key={selectedId}
                quillRef={quillRef}
                value={contentHtml}
                uncontrolled
                onChange={handleHtmlChange}
                onActivate={checkLiveNoteTimestamp}
                onPasteFiles={handlePasteFiles}
                onKeepUploadedFile={keepUploadedFile}
                onSensitiveClick={(selectedText) => openAddCredentials(selectedText)}
                enableMentions={false}
                toolbarMode="notes"
                placeholder="Write here. Paste an image to keep it in the note."
                minHeight={160}
                maxHeight={1800}
                instanceClass="notes-rich-editor"
              />
              </div>
              <div className="mt-0 flex-shrink-0 max-h-[32%] overflow-y-auto space-y-1.5 border-t border-gray-100 dark:border-gray-800 px-3 py-1.5">
                {aiCustomOpen ? (
                  <div className="rounded-2xl border border-violet-200/80 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 p-3 shadow-sm dark:border-violet-800/70 dark:from-violet-950/50 dark:via-gray-900 dark:to-fuchsia-950/30">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-300">
                      AI customization
                    </p>
                    <textarea
                      value={aiPrompt}
                      onChange={(event) => setAiPrompt(event.target.value)}
                      disabled={aiBusy}
                      rows={3}
                      placeholder="Tell AI how to rewrite this note… e.g. group as vegetables, fruits, and gifts"
                      className="mt-2 w-full resize-none rounded-xl border border-violet-200 bg-white/90 px-3 py-2 text-sm text-gray-800 outline-none placeholder:text-gray-400 focus:ring-2 focus:ring-violet-400/40 dark:border-violet-800 dark:bg-gray-900/80 dark:text-gray-100"
                    />
                    <div className="mt-2 flex justify-end">
                      <button
                        type="button"
                        onClick={() => runNoteAi('PROMPT')}
                        disabled={aiBusy}
                        className="rounded-lg bg-violet-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-violet-700 disabled:opacity-60"
                      >
                        Send
                      </button>
                    </div>
                  </div>
                ) : null}
                {aiBusy ? (
                  <div className="px-0.5">
                    <div className="mb-1 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-300">
                      <span>Working on this note</span>
                      <span>{aiProgress}%</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-violet-100 dark:bg-violet-950/70">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-violet-400 transition-all duration-200"
                        style={{ width: `${aiProgress}%` }}
                      />
                    </div>
                  </div>
                ) : null}
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => runNoteAi('STRUCTURE')}
                    disabled={aiBusy}
                    className="flex-1 px-2 py-1 text-[10px] font-semibold rounded-lg border border-violet-200 bg-violet-50 text-violet-800 hover:bg-violet-100 disabled:opacity-60 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-200"
                  >
                    Structure this note
                  </button>
                  <button
                    type="button"
                    onClick={() => setAiCustomOpen((open) => !open)}
                    disabled={aiBusy}
                    className={`flex-1 px-2 py-1 text-[10px] font-semibold rounded-lg border disabled:opacity-60 ${
                      aiCustomOpen
                        ? 'border-violet-400 bg-violet-600 text-white'
                        : 'border-violet-200 bg-white text-violet-800 hover:bg-violet-50 dark:border-violet-800 dark:bg-gray-900 dark:text-violet-200'
                    }`}
                  >
                    AI Customization
                  </button>
                </div>
                {(callPeople.length > 0 || buyingItems.length > 0 || taskItems.length > 0) ? (
                  <div className="flex flex-wrap gap-1.5">
                    {callPeople.length > 0 ? (
                      <button
                        type="button"
                        onClick={openCallReminderPicker}
                        disabled={aiBusy || viewingOriginal}
                        className="px-2 py-1 text-[10px] font-semibold rounded-lg border border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100 disabled:opacity-60 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200"
                      >
                        Add to call reminder
                      </button>
                    ) : null}
                    {buyingItems.length > 0 ? (
                      <span className="px-2 py-1 text-[10px] font-semibold rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
                        {buyingItems.length} cart item{buyingItems.length === 1 ? '' : 's'}
                      </span>
                    ) : null}
                    {taskItems.length > 0 ? (
                      <button
                        type="button"
                        onClick={openNoteTasks}
                        disabled={aiBusy || viewingOriginal}
                        className="px-2 py-1 text-[10px] font-semibold rounded-lg border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 disabled:opacity-60 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200"
                      >
                        Add to task
                      </button>
                    ) : null}
                  </div>
                ) : null}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      Sensitive info
                    </p>
                    <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                      Make your credentials more secure and save from mistake deletion.
                      {pinEnabled ? ' Updates and deletion require your PIN.' : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPinModal(pinEnabled ? 'change' : 'setup')}
                    className="flex-shrink-0 px-2 py-1 text-[10px] font-semibold rounded-lg border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40"
                  >
                    {pinEnabled ? 'Change PIN' : 'Set PIN'}
                  </button>
                </div>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 items-start">
                {(expandedSensitiveId
                  ? [
                    ...sensitiveBlocks.filter((block) => block.id === expandedSensitiveId),
                    ...sensitiveBlocks.filter((block) => block.id !== expandedSensitiveId),
                  ]
                  : sensitiveBlocks
                ).map((block) => {
                    const expanded = expandedSensitiveId === block.id;
                    const entries = Array.isArray(block.entries) ? block.entries : [];
                    const files = Array.isArray(block.attachments) ? block.attachments : [];
                    return (
                      <div
                        key={block.id}
                        id={`sensitive-block-${block.id}`}
                        className={`min-w-0 self-start rounded-xl border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/30 overflow-hidden ${expanded ? 'col-span-full' : ''}`}
                      >
                        <div className="flex items-center gap-1 px-2 py-1.5">
                          <button
                            type="button"
                            onClick={() => toggleSensitiveBlock(block.id)}
                            className="min-w-0 flex-1 flex items-center gap-1.5 text-left"
                            aria-expanded={expanded}
                          >
                            <span className="text-amber-700 dark:text-amber-300 flex-shrink-0" aria-hidden="true">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                              </svg>
                            </span>
                            <span className="min-w-0 flex-1 text-xs font-medium text-gray-900 dark:text-white truncate">
                              {noteSearch.trim() ? highlightPlainText(block.title, noteSearch) : block.title}
                            </span>
                            <span className="text-[9px] text-gray-400 flex-shrink-0 hidden sm:inline">
                              {entries.length}
                            </span>
                            <svg
                              className={`w-3.5 h-3.5 flex-shrink-0 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => openSensitiveEditor(block)}
                            className="flex-shrink-0 p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                            aria-label={`Update ${block.title}`}
                          >
                            <EditPencilIcon />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeSensitiveBlock(block.id)}
                            className="text-gray-400 hover:text-rose-500 px-0.5 flex-shrink-0"
                            aria-label={`Remove ${block.title}`}
                          >
                            ×
                          </button>
                        </div>
                        {expanded ? (
                          <div className="border-t border-amber-200/80 dark:border-amber-800/50 px-1.5 py-1.5">
                            <div className={`grid gap-1 ${expanded ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4' : 'grid-cols-1'}`}>
                              {entries.map((entry) => {
                                const revealed = Boolean(entry && entry.valueVisible);
                                return (
                                  <div
                                    key={entry.id}
                                    className="rounded-md bg-white/80 dark:bg-gray-900/50 px-1.5 py-1 min-w-0"
                                  >
                                    <p className="text-[10px] font-medium text-gray-700 dark:text-gray-200 truncate">
                                      {entry.key}
                                    </p>
                                    <div className="mt-0.5 flex items-center gap-0.5">
                                      <span className={`min-w-0 flex-1 text-[10px] truncate ${
                                        revealed ? 'text-gray-800 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'
                                      }`}>
                                        {revealed ? (entry.value || '') : '••••••••'}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => copySensitiveValue(
                                          entry,
                                          !entry.valueVisible && Boolean(block.askPinOnCopy),
                                        )}
                                        className="flex-shrink-0 px-1 py-0.5 text-[9px] font-semibold rounded bg-white dark:bg-gray-800 border border-amber-200 dark:border-amber-800 text-gray-800 dark:text-gray-100"
                                      >
                                        {copiedSecretId === entry.id ? 'Copied' : (revealed ? 'Copy' : 'Open')}
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            {files.length ? (
                              <ul className="mt-2 space-y-1">
                                {files.map((file) => (
                                  <li key={file.id || file.fileName}>
                                    <button
                                      type="button"
                                      onClick={() => openCredentialAttachment(block, file)}
                                      className="w-full flex items-center gap-2 rounded-md bg-white/80 dark:bg-gray-900/50 px-1.5 py-1 text-left"
                                    >
                                      <FileTypeIcon fileName={file.fileName} fileType={file.fileType} size="sm" />
                                      <span className="min-w-0 flex-1 truncate text-[10px] font-medium text-gray-800 dark:text-gray-100">
                                        {file.fileName || 'File'}
                                      </span>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => openSensitiveEditor(block)}
                              className="mt-1 text-[10px] font-semibold text-primary-600 dark:text-primary-400 hover:underline"
                            >
                              Update
                            </button>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
              </div>
            </div>

            <aside className="w-full md:w-[20%] md:flex-none min-h-[220px] md:min-h-0 border-t md:border-t-0 md:border-l border-gray-200/80 dark:border-gray-800 bg-slate-50 dark:bg-gray-950 p-3 flex flex-col overflow-hidden">
              <input
                ref={attachInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(event) => {
                  addAttachments(event.target.files);
                  event.target.value = '';
                }}
              />
              <div className="min-h-0 flex-1 flex flex-col overflow-hidden rounded-2xl border border-gray-200/90 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <div className="px-3.5 py-3 border-b border-gray-100 dark:border-gray-800">
                <p className="text-xs font-semibold text-gray-800 dark:text-gray-100">Attach</p>
                <p className="text-[10px] text-gray-500 dark:text-gray-400">Files, images, links, credentials</p>
              </div>
              <div className="p-2">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={handleAttachClick}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleAttachClick();
                    }
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    event.dataTransfer.dropEffect = 'copy';
                    setAttachDragOver(true);
                  }}
                  onDragLeave={() => setAttachDragOver(false)}
                  onDrop={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setAttachDragOver(false);
                    addAttachments(event.dataTransfer?.files);
                  }}
                  className={`rounded-xl border-2 border-dashed px-2 py-4 text-center cursor-pointer transition-colors ${
                    attachDragOver
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/30'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 hover:border-primary-400'
                  }`}
                >
                  <p className="text-[11px] font-semibold text-gray-800 dark:text-gray-100">
                    {uploading ? 'Uploading…' : 'Drop or click'}
                  </p>
                  <p className="mt-0.5 text-[10px] text-gray-500 dark:text-gray-400">
                    File or image
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleAddLink}
                  className="mt-2 w-full px-2 py-1.5 text-[11px] font-semibold rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Add link
                </button>
                <button
                  type="button"
                  onClick={() => openAddCredentials()}
                  className="mt-2 w-full px-2 py-1.5 text-[11px] font-semibold rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-900/50"
                >
                  Add credentials
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-3 space-y-2">
                {attachments.length === 0 ? (
                  <p className="px-1 text-[11px] text-gray-400">
                    No files yet. Paste an image in the note to keep it here too.
                  </p>
                ) : attachments.map((item) => {
                  const isLink = String(item.fileType || '').toUpperCase() === 'LINK';
                  const href = item.linkUrl || item.fileUrl;
                  if (isDisplayableImage(item)) {
                    const renaming = editingAttachmentId === item.id;
                    return (
                      <div key={item.id} className="relative group rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                        <button
                          type="button"
                          className="block w-full"
                          onClick={() => openAttachmentViewer(item)}
                        >
                          <PresignedImage
                            fileKey={item.fileUrl}
                            alt={item.fileName || ''}
                            className="w-full h-20 object-cover"
                          />
                        </button>
                        <div className="flex items-center gap-1 px-2 py-1">
                          {renaming ? (
                            <input
                              defaultValue={item.fileName || ''}
                              autoFocus
                              className="min-w-0 flex-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-1 py-0.5 text-[10px] text-gray-900 dark:text-white"
                              onClick={(event) => event.stopPropagation()}
                              onBlur={(event) => renameAttachment(item.id, event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  event.preventDefault();
                                  renameAttachment(item.id, event.target.value);
                                }
                                if (event.key === 'Escape') {
                                  event.preventDefault();
                                  setEditingAttachmentId(null);
                                }
                              }}
                            />
                          ) : (
                            <p className="min-w-0 flex-1 text-[10px] text-gray-600 dark:text-gray-300 truncate">
                              {noteSearch.trim() ? highlightPlainText(item.fileName || '', noteSearch) : item.fileName}
                            </p>
                          )}
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              setEditingAttachmentId(item.id);
                            }}
                            className="flex-shrink-0 p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                            aria-label="Edit file name"
                          >
                            <EditPencilIcon />
                          </button>
                        </div>
                        <button
                          type="button"
          onClick={(event) => {
            event.stopPropagation();
            removeAttachment(item.id);
          }}
                          className="absolute top-1 right-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white text-xs"
                          aria-label="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    );
                  }
                  const renaming = editingAttachmentId === item.id;
                  return (
                    <div
                      key={item.id}
                      className="relative flex items-start gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-1.5 py-1.5"
                    >
                      {isLink && item.previewImageUrl ? (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0"
                        >
                          <img
                            src={item.previewImageUrl}
                            alt=""
                            className="w-10 h-10 rounded object-cover bg-gray-100"
                          />
                        </a>
                      ) : (
                        <FileTypeIcon fileName={item.fileName} size="sm" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start gap-1">
                          {renaming ? (
                            <input
                              defaultValue={item.fileName || ''}
                              autoFocus
                              className="min-w-0 flex-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-1 py-0.5 text-[11px] text-gray-900 dark:text-white"
                              onBlur={(event) => renameAttachment(item.id, event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  event.preventDefault();
                                  renameAttachment(item.id, event.target.value);
                                }
                                if (event.key === 'Escape') {
                                  event.preventDefault();
                                  setEditingAttachmentId(null);
                                }
                              }}
                            />
                          ) : isLink ? (
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="min-w-0 flex-1 text-[11px] font-medium text-primary-600 dark:text-primary-300 break-all hover:underline"
                            >
                              {noteSearch.trim()
                                ? highlightPlainText(item.fileName || href || '', noteSearch)
                                : (item.fileName || href)}
                            </a>
                          ) : (
                            <button
                              type="button"
                              onClick={() => openAttachmentViewer(item)}
                              className="min-w-0 flex-1 text-left text-[11px] font-medium text-gray-800 dark:text-gray-100 truncate hover:underline"
                            >
                              {noteSearch.trim()
                                ? highlightPlainText(item.fileName || 'File', noteSearch)
                                : (item.fileName || 'File')}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setEditingAttachmentId(item.id)}
                            className="flex-shrink-0 p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                            aria-label="Edit file name"
                          >
                            <EditPencilIcon />
                          </button>
                        </div>
                        <p className="text-[10px] text-gray-400 truncate">
                          {isLink ? (item.domain || 'Link') : (formatFileSize(item.fileSize) || item.fileType)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(item.id)}
                        className="text-gray-400 hover:text-rose-500 text-sm leading-none px-0.5"
                        aria-label="Remove attachment"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
              </div>
            </aside>
          </div>
        </div>
      )}
      </div>
      {!selectedId ? (
        <aside className="w-full md:w-[24%] md:flex-none min-h-[240px] md:min-h-0 border-t md:border-t-0 md:border-l border-gray-200/80 dark:border-gray-800 bg-slate-50 dark:bg-gray-950 p-3 overflow-hidden">
          <div className="h-full min-h-0 overflow-hidden rounded-2xl border border-sky-100 bg-white shadow-sm dark:border-sky-900/40 dark:bg-gray-900">
            <NeedToCallList />
          </div>
        </aside>
      ) : taskComposerOpen ? (
        <aside className="w-full md:w-[min(420px,40%)] md:flex-none min-h-[280px] md:min-h-0 border-t md:border-t-0 md:border-l border-gray-200 dark:border-gray-800 overflow-hidden">
          <TaskCreationDialog
            variant="sidebar"
            isOpen={taskComposerOpen}
            initialTitle={taskSeedTitle}
            onClose={() => {
              pendingTaskTitlesRef.current = [];
              setTaskSeedTitle('');
              setTaskComposerOpen(false);
            }}
            onSaved={() => {
              const next = pendingTaskTitlesRef.current;
              if (next.length) {
                pendingTaskTitlesRef.current = next.slice(1);
                setTaskSeedTitle(next[0]);
                setTaskComposerOpen(true);
                return;
              }
              setTaskSeedTitle('');
              setTaskComposerOpen(false);
            }}
          />
        </aside>
      ) : null}
      </div>

      {viewerIndex != null && (viewerItems || fileAttachments).length > 0 ? (
        <NoteAttachmentViewer
          items={viewerItems || fileAttachments}
          index={Math.min(viewerIndex, (viewerItems || fileAttachments).length - 1)}
          onIndexChange={setViewerIndex}
          onClose={() => {
            setViewerIndex(null);
            setViewerItems(null);
          }}
        />
      ) : null}

      <CredentialRevealModal
        open={Boolean(revealSecret)}
        title={revealSecret?.title}
        secret={revealSecret?.secret}
        onClose={() => setRevealSecret(null)}
        onCopied={() => {
          if (revealSecret?.entryId) {
            setCopiedSecretId(revealSecret.entryId);
            window.setTimeout(() => {
              setCopiedSecretId((current) => (current === revealSecret.entryId ? null : current));
            }, 1600);
          }
        }}
      />
      <CredentialPinModal
        open={Boolean(pinModal)}
        mode={pinModal || 'setup'}
        onClose={() => {
          pendingPinActionRef.current = null;
          setPinModal(null);
        }}
        onSuccess={() => {
          if (pinModal === 'verify') {
            const action = pendingPinActionRef.current;
            pendingPinActionRef.current = null;
            action?.();
            return;
          }
          setPinEnabled(true);
        }}
      />
      <SensitiveBlockModal
        open={secretModalOpen}
        seedText={secretSeedText}
        initialBlock={editingSensitiveBlock}
        pinEnabled={pinEnabled}
        onClose={() => {
          setSecretModalOpen(false);
          setSecretSeedText('');
          setEditingSensitiveBlock(null);
        }}
        onSave={addSensitiveBlock}
      />
      <ShareNoteModal
        isOpen={Boolean(shareTarget)}
        note={shareTarget}
        onClose={() => setShareTarget(null)}
        onShared={(result) => {
          const saved = result?.note;
          if (saved?.id) {
            setNotes((prev) => {
              const next = redactNoteForList(saved);
              if (prev.some((item) => item.id === saved.id)) {
                return prev.map((item) => (item.id === saved.id ? next : item));
              }
              return [next, ...prev];
            });
            if (selectedIdRef.current === saved.id) {
              setShareMeta({
                shareKind: String(saved.shareKind || '').toUpperCase(),
                sharedByName: saved.sharedByName || '',
                userId: saved.userId || null,
                sharedMemberIds: Array.isArray(saved.sharedMemberIds) ? saved.sharedMemberIds : [],
              });
            }
          }
        }}
      />
      <ConfirmModal
        isOpen={Boolean(deleteTarget)}
        type="danger"
        title={
          deleteTarget?.shareKind === 'LIVE' && deleteTarget?.userId && deleteTarget.userId !== currentUserId
            ? 'Remove shared note'
            : 'Delete note'
        }
        message={
          deleteTarget?.shareKind === 'LIVE' && deleteTarget?.userId && deleteTarget.userId !== currentUserId
            ? `Remove “${deleteTarget?.title || 'this note'}” from your list? The owner and other people keep it.`
            : `Delete “${deleteTarget?.title || 'this note'}”? This cannot be undone.`
        }
        confirmText={
          deleteTarget?.shareKind === 'LIVE' && deleteTarget?.userId && deleteTarget.userId !== currentUserId
            ? 'Remove'
            : 'Delete'
        }
        cancelText="Cancel"
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDeleteNote}
      />
      <CallReminderPeopleModal
        open={callReminderOpen}
        people={callPeople}
        saving={callReminderSaving}
        onClose={() => setCallReminderOpen(false)}
        onConfirm={confirmCallReminderPeople}
      />
      <AddLinkModal
        open={linkModalOpen}
        seedUrl={linkSeedUrl}
        onClose={() => {
          setLinkModalOpen(false);
          setLinkSeedUrl('');
        }}
        onSave={saveNoteLink}
      />
    </div>
  );
};

export default NotesHomePanel;
